// ============================================================================
// TeamMemberFullScreenPage.jsx — Full-screen Team Member Detail Overlay
// ============================================================================
// WHERE TO PLACE: src/pages/team/TeamMemberFullScreenPage.jsx
// ============================================================================
// Fixes applied:
//   #1  Save button always visible, disabled when no changes
//   #2  Address autocomplete from address_directory table
//   #3  "Education" drawer renamed to "Resume"
//   #4  Resume: Degrees, Employers, Gap stacked vertically (no tabs)
//   #5  Gap = manual field (experienceGapYears + notes)
//   #6  Total Experience computed and shown in Core
//   #7  Salary history read-only (no add/edit/delete)
//   #8  Add Review button fully functional
//   #9  Add Evaluation button fully functional
// ============================================================================

import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  X, ChevronDown, ChevronRight, Plus, Trash2, Save,
  Key, Shield, MapPin, DollarSign, Star, FileText,
  AlertTriangle, Check, Loader2, Search, GraduationCap,
  Briefcase, ExternalLink
} from 'lucide-react';
import { BrioDatePicker, BrioMonthPicker, BrioBadge } from '../../components/BrioComponents';
import ExpertiseMultiSelect from '../../components/ExpertiseMultiSelect';
import ProfilePhotoCrop from '../../components/ProfilePhotoCrop';
import { useTeamMemberProfile } from '../../hooks/useTeamMemberProfile';
import { useExpertiseOptions } from '../../hooks/useExpertiseOptions';
import { parseMbtiType, getPersonalityHex, normalizeExpertiseArray } from '../../utils/personalityHelpers';
import { adminResetPassword } from '../../hooks/useSupabase';
import { useSalaryLedger } from '../../hooks/useSalaryLedger';
import { useUserRoleHistory } from '../../hooks/useUserRoleHistory';

// ============================================================================
// STYLE CONSTANTS — BRIO Design System
// ============================================================================
const BRIO = {
  gold: 'var(--brio-gold)',
  goldHover: 'var(--brio-gold-hover)',
  goldLight: 'var(--brio-gold-light)',
  goldMedium: 'rgba(159, 144, 42, 0.2)',
  white: 'var(--brio-white)',
  black: 'var(--brio-black)',
  grey: 'var(--brio-grey)',
  greyLight: 'var(--brio-grey-light)',
  greyMuted: 'var(--brio-grey)',
  border: 'var(--brio-border)',
  borderStrong: 'rgba(0, 0, 0, 0.15)',
  danger: 'var(--brio-red)',
  dangerBg: 'rgba(147, 73, 72, 0.08)',
  success: 'var(--brio-green)',
  successBg: 'rgba(132, 164, 140, 0.08)',
  warningBg: 'rgba(236, 210, 136, 0.15)',
  infoBg: 'rgba(104, 149, 129, 0.08)',
  font: "'Raleway', sans-serif",
};

const INPUT_STYLE = {
  width: '100%',
  padding: '7px 10px',
  border: `1px solid ${BRIO.border}`,
  borderRadius: 0,
  fontSize: '13px',
  lineHeight: '1.4',
  background: BRIO.white,
  color: BRIO.black,
  boxSizing: 'border-box',
  fontFamily: BRIO.font,
  transition: 'border-color 0.15s',
};

const SELECT_STYLE = { ...INPUT_STYLE, appearance: 'auto' };

const LABEL_STYLE = {
  fontSize: '11px',
  fontWeight: 500,
  color: BRIO.grey,
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  marginBottom: '4px',
  display: 'block',
  fontFamily: BRIO.font,
};

const SECTION_LABEL_STYLE = {
  fontSize: '12px',
  fontWeight: 500,
  color: BRIO.black,
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  marginBottom: '10px',
  marginTop: '20px',
  paddingBottom: '6px',
  borderBottom: `1px solid ${BRIO.border}`,
  fontFamily: BRIO.font,
};

// ============================================================================
// ROLE-BASED EVALUATION CRITERIA
// Each role has categories, each category has criteria items rated 1-5.
// ============================================================================
const ROLE_EVALUATION_CRITERIA = {
  'Junior Architect': [
    { category: 'Independence & Direction', items: [
      'Works accurately within a defined brief without requiring repeated clarification',
      'Raises questions promptly rather than proceeding under uncertainty',
      'Demonstrates improving self-sufficiency on standard tasks over time',
    ]},
    { category: 'Quality & Precision', items: [
      'Produces drawings and documents that meet the standard set by the project team',
      'Applies consistent file discipline, annotation, and naming conventions',
      'Accepts and integrates correction without resistance',
    ]},
    { category: 'Project Understanding', items: [
      'Understands the purpose and context of assigned tasks within the project',
      'Can identify how their work connects to adjacent decisions or outputs',
      'Shows growing awareness of project-wide intent',
    ]},
    { category: 'Team Integration', items: [
      'Communicates clearly and reliably within the team',
      'Flags blockers and dependencies without prompting',
      'Responds to feedback in a way that raises the quality of subsequent work',
    ]},
  ],
  'Medior Architect': [
    { category: 'Independence & Self-Direction', items: [
      'Completes assigned packages with limited correction required',
      'Manages own workload and flags risks before they affect the team',
      'Does not require close supervision on standard tasks',
    ]},
    { category: 'Quality & Coordination', items: [
      'Delivers work that is internally coordinated and consistent with project intent',
      'Maintains reliability across deliverables — completeness, accuracy, and timing',
      'Identifies and resolves minor conflicts in information before they escalate',
    ]},
    { category: 'Project Understanding', items: [
      'Understands project intent and can apply it to tasks without direct instruction',
      'Tracks information flows and knows where dependencies lie',
      'Anticipates downstream implications of current decisions',
    ]},
    { category: 'Team Contribution', items: [
      'Actively supports coordination across the team',
      'Reviews and supports junior output as a matter of course',
      'Communicates clearly with consultants and external parties',
    ]},
  ],
  'Project Architect': [
    { category: 'Independent Project Leadership', items: [
      'Has led a small project from brief to delivery without Senior Architect support',
      'Makes design, coordination, and client decisions independently at appropriate project scales',
      'Identifies the limits of their independent scope and escalates to Associate or Principal when warranted',
    ]},
    { category: 'Operational Leadership', items: [
      'Structures the project workflow clearly and maintains it across the project lifecycle',
      'Sequences tasks and deliverables in a way that keeps the team productive',
      'Makes operational decisions without escalating unnecessarily',
    ]},
    { category: 'Coordination & Information Management', items: [
      'Manages consultant and client information flows with discipline',
      'Ensures decisions are documented and communicated to the right parties',
      'Identifies coordination gaps and closes them before they affect output',
    ]},
    { category: 'Delivery Accountability', items: [
      'Takes direct responsibility for the completeness and timing of project outputs',
      'Identifies delivery risks early and acts to resolve them',
      'Maintains quality standards under schedule and resource pressure',
    ]},
    { category: 'Team Management', items: [
      'Distributes work clearly and ensures team members understand their scope',
      'Monitors team output and intervenes where quality or momentum is at risk',
      'Creates the conditions for others to work with clarity',
    ]},
  ],
  'Senior Architect': [
    { category: 'Team Leadership', items: [
      'Leads a team of architects across multiple concurrent projects with clarity and authority',
      'Sets and maintains working standards across all projects in their portfolio',
      'Distributes responsibility within the team effectively and adjusts when conditions change',
    ]},
    { category: 'Project Accountability', items: [
      'Bears sole responsibility for the quality and delivery of all projects in their portfolio',
      'Makes consequential design and delivery decisions without requiring escalation',
      'Identifies and resolves problems across projects before they compound',
    ]},
    { category: 'Scheduling & Budgeting', items: [
      'Sets and maintains realistic project programmes across their full portfolio',
      'Tracks fee expenditure and flags variances before they become overruns',
      'Makes schedule and resource adjustments at project level without requiring escalation',
    ]},
    { category: 'Design & Technical Judgement', items: [
      'Resolves technically and spatially complex problems with independent rigour',
      'Maintains design integrity across multiple projects simultaneously',
      'Produces decisions and outputs that require minimal revision at this level',
    ]},
    { category: 'Team Development', items: [
      'Actively develops the capability of each team member through direct engagement',
      'Creates a team environment where quality, initiative, and clarity are the norm',
      'Identifies when individuals are ready for greater responsibility and acts on it',
    ]},
  ],
  'Associate': [
    { category: 'Independent Project Leadership', items: [
      'Leads projects independently — driving design direction, managing teams, and making delivery decisions at the level of a Senior Architect',
      'Balances project-level design and coordination decisions against their impact on adjacent projects, timelines, and studio-wide capacity',
    ]},
    { category: 'Oversight of Senior Architects', items: [
      'Maintains clear visibility across all Senior Architect-led teams and their project portfolios',
      'Identifies where Senior Architects need support, challenge, or intervention — and acts accordingly',
      'Holds Senior Architects accountable to quality and delivery standards without micromanaging',
    ]},
    { category: 'Office-Wide Mentorship', items: [
      'Engages in meaningful development conversations with team members at all levels',
      'Identifies capability gaps across the office and creates structured pathways to address them',
      'Models the standards and behaviours expected of the studio as a whole',
    ]},
    { category: 'Project Negotiations', items: [
      'Attends negotiations with clients and contractors and contributes cross-project context',
      'Ensures that agreements made do not conflict with existing studio commitments or capacity',
      'Provides grounded input on programme and scope without overriding the negotiation lead',
    ]},
    { category: 'Fee Visibility & Resource Allocation', items: [
      'Maintains an accurate overview of fee allocation and utilisation across all projects',
      'Uses fee intelligence to make timely decisions about moving people between teams',
      'Ensures that resource shifts are communicated clearly and do not destabilise individual projects',
    ]},
  ],
  'Business Associate': [
    { category: 'Pipeline Development', items: [
      'Builds a pipeline of opportunities consistent with the studio\'s strategic direction',
      'Qualifies opportunities by design fit and ambition — not only commercial value',
      'Maintains continuity in incoming work without creating strategic drift',
    ]},
    { category: 'Relationship Management', items: [
      'Develops and sustains long-term trust with clients and collaborators',
      'Represents the studio credibly across typologies and client types',
      'Knows when to involve a Principal and structures introductions accordingly',
    ]},
    { category: 'Strategic Alignment', items: [
      'Understands the studio\'s design ambitions and positions opportunities accordingly',
      'Screens potential work against capacity, values, and long-term direction',
      'Provides Principals with clear, considered intelligence on the market and pipeline',
    ]},
    { category: 'Design & Practice Literacy', items: [
      'Engages in practice conversations with sufficient depth to be credible',
      'Understands what distinguishes BRIO\'s work and can articulate it externally',
      'Bridges the gap between commercial relationship and design intent',
    ]},
  ],
  'Principal / Partner': [
    { category: 'Domain Leadership', items: [
      'Leads their primary domain (design, delivery, or business) with clear authority',
      'Sets standards and makes decisions that others can use as a reference point',
      'Contributes actively to decisions outside their primary domain',
    ]},
    { category: 'Strategic & Cultural Contribution', items: [
      'Makes or significantly shapes decisions with multi-year consequences for the studio',
      'Holds and communicates the design values and cultural identity of the practice',
      'Identifies strategic opportunities or risks before they become urgent',
    ]},
    { category: 'Resolution of Ambiguity', items: [
      'Acts decisively in situations where the stakes are high and clarity is absent',
      'Arbitrates between competing priorities without avoiding the hard calls',
      'Creates clarity for the team in moments of uncertainty or conflict',
    ]},
    { category: 'Practice Stewardship', items: [
      'Takes responsibility for the long-term health of the studio — not just its current output',
      'Develops the next generation of leadership within the practice',
      'Holds the reputation, relationships, and institutional memory of the studio',
    ]},
  ],
};

// Map DB role names to evaluation criteria keys
function getEvalRoleKey(jobRole) {
  if (!jobRole) return null;
  const role = String(jobRole).replace(/\s*\(\d+\)$/, '').trim();
  const ALIASES = {
    'Director': 'Principal / Partner',
    'Partner': 'Principal / Partner',
    'Partner / Director': 'Principal / Partner',
    'Associate Architect': 'Associate',
  };
  return ALIASES[role] || (ROLE_EVALUATION_CRITERIA[role] ? role : null);
}

// ============================================================================
// AUTO-RESIZE TEXTAREA — grows vertically to fit content, no scrollbar
// ============================================================================
function AutoResizeTextarea({ value, onChange, onBlur, disabled, placeholder, minRows = 2, style: styleProp, ...rest }) {
  const ref = useRef(null);
  const resize = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = el.scrollHeight + 'px';
  }, []);

  // Resize on value changes (typing, programmatic updates, data rehydration)
  useEffect(() => { resize(); }, [value, resize]);

  // Resize once after initial mount (pre-filled text)
  useEffect(() => {
    // Use rAF to ensure DOM has painted with correct font metrics
    const id = requestAnimationFrame(resize);
    return () => cancelAnimationFrame(id);
  }, [resize]);

  const mergedStyle = {
    ...INPUT_STYLE,
    resize: 'none',
    overflow: 'hidden',
    minHeight: `${minRows * 1.4 * 13 + 14}px`, // lineHeight × fontSize × rows + padding
    ...styleProp,
  };

  return (
    <textarea
      ref={ref}
      value={value}
      onChange={onChange}
      onBlur={onBlur}
      onInput={resize}
      disabled={disabled}
      placeholder={placeholder}
      rows={minRows}
      style={mergedStyle}
      {...rest}
    />
  );
}

// ============================================================================
// STANDARD BRIO JOB ROLES (must match App.js jobRoles list)
// ============================================================================
const BRIO_JOB_ROLES = [
  'Intern',
  'Junior Architect',
  'Medior Architect',
  'Project Architect',
  'Senior Architect',
  'Associate',
  'Partner',
  'Director',
  'Support',
  'Model Maker',
  'Visualiser',
  'Others',
];

// ============================================================================
// PERMISSION HELPERS
// ============================================================================
function canEdit(currentUserRole) {
  return currentUserRole === 'admin' || currentUserRole === 'admin-pa';
}
function canDelete(currentUserRole) {
  return currentUserRole === 'admin';
}

// ============================================================================
// SUB-COMPONENTS
// ============================================================================

function Field({ label, children, span2 }) {
  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      gap: '2px',
      gridColumn: span2 ? 'span 2' : undefined,
    }}>
      <label style={LABEL_STYLE}>{label}</label>
      {children}
    </div>
  );
}

function FieldGrid({ children, cols = 2 }) {
  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: `repeat(${cols}, 1fr)`,
      gap: '14px 20px',
      padding: '16px 0',
    }}>
      {children}
    </div>
  );
}

function TabBar({ tabs, active, onChange }) {
  return (
    <div style={{
      display: 'flex',
      gap: '2px',
      borderBottom: `1px solid ${BRIO.border}`,
      marginBottom: '8px',
    }}>
      {tabs.map(t => (
        <button
          key={t.id}
          onClick={() => onChange(t.id)}
          style={{
            padding: '8px 16px',
            background: 'transparent',
            border: 'none',
            borderBottom: active === t.id ? `2px solid ${BRIO.gold}` : '2px solid transparent',
            color: active === t.id ? BRIO.black : BRIO.grey,
            fontSize: '12px',
            fontWeight: active === t.id ? 500 : 300,
            cursor: 'pointer',
            fontFamily: BRIO.font,
            textTransform: 'lowercase',
            transition: 'all 0.15s',
          }}
        >
          {t.label}
        </button>
      ))}
    </div>
  );
}

function RoleBadge({ role }) {
  const colors = {
    admin: { bg: 'rgba(236, 210, 136, 0.15)', text: 'var(--brio-orange)', border: 'var(--brio-yellow)' },
    'admin-pa': { bg: 'rgba(236, 210, 136, 0.15)', text: 'var(--brio-orange)', border: 'var(--brio-yellow)' },
    supervisor: { bg: 'var(--brio-gold-light)', text: 'var(--brio-gold)', border: 'var(--brio-gold-light)' },
    user: { bg: 'rgba(132, 164, 140, 0.08)', text: 'var(--brio-green)', border: 'rgba(132, 164, 140, 0.3)' },
  };
  const c = colors[role] || colors.user;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: '4px',
      padding: '3px 10px', fontSize: '11px', fontWeight: 500, fontFamily: BRIO.font,
      background: c.bg, color: c.text, border: `1px solid ${c.border}`,
      borderRadius: 0, textTransform: 'lowercase',
    }}>
      <Shield style={{ width: 12, height: 12 }} />{role}
    </span>
  );
}

function StatusBadge({ isActive }) {
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: '4px',
      padding: '3px 10px', fontSize: '11px', fontWeight: 500, fontFamily: BRIO.font,
      background: isActive ? 'rgba(132, 164, 140, 0.08)' : 'rgba(147, 73, 72, 0.08)',
      color: isActive ? 'var(--brio-green)' : 'var(--brio-red)',
      border: `1px solid ${isActive ? 'rgba(132, 164, 140, 0.3)' : 'rgba(147, 73, 72, 0.2)'}`,
      borderRadius: 0,
    }}>
      {isActive ? '● active' : '○ inactive'}
    </span>
  );
}

function Drawer({ title, icon: Icon, open, onToggle, children, count }) {
  return (
    <div style={{ borderBottom: `1px solid ${BRIO.border}`, background: BRIO.white }}>
      <button
        onClick={onToggle}
        style={{
          width: '100%', display: 'flex', alignItems: 'center', gap: '10px',
          padding: '14px 20px',
          background: open ? BRIO.goldLight : 'transparent',
          border: 'none', cursor: 'pointer', fontFamily: BRIO.font,
          fontSize: '14px', fontWeight: open ? 500 : 400,
          color: BRIO.black, textTransform: 'lowercase',
          transition: 'background 0.15s', textAlign: 'left',
        }}
      >
        {Icon && <Icon style={{ width: 16, height: 16, color: BRIO.gold, flexShrink: 0 }} />}
        <span style={{ flex: 1 }}>{title}</span>
        {count !== undefined && (
          <span style={{
            fontSize: '10px', padding: '2px 7px', background: BRIO.goldMedium,
            color: BRIO.goldHover, borderRadius: '10px', fontWeight: 500,
          }}>{count}</span>
        )}
        {open
          ? <ChevronDown style={{ width: 14, height: 14, color: BRIO.grey }} />
          : <ChevronRight style={{ width: 14, height: 14, color: BRIO.grey }} />}
      </button>
      {open && <div style={{ padding: '4px 20px 20px' }}>{children}</div>}
    </div>
  );
}

function Toast({ message, type, onDismiss }) {
  if (!message) return null;
  const bg = type === 'error' ? BRIO.dangerBg : BRIO.successBg;
  const color = type === 'error' ? 'var(--brio-red)' : 'var(--brio-green)';
  const border = type === 'error' ? 'rgba(147, 73, 72, 0.2)' : 'rgba(132, 164, 140, 0.3)';
  return (
    <div style={{
      position: 'fixed', bottom: 24, right: 24, zIndex: 100002,
      padding: '12px 20px', background: bg, color,
      border: `1px solid ${border}`, borderRadius: 0,
      fontSize: '13px', fontFamily: BRIO.font,
      boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
      display: 'flex', alignItems: 'center', gap: '8px', maxWidth: '400px',
    }}>
      {type === 'error' ? <AlertTriangle style={{ width: 14, height: 14 }} /> : <Check style={{ width: 14, height: 14 }} />}
      <span style={{ flex: 1 }}>{message}</span>
      <button onClick={onDismiss} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px', color }}>
        <X style={{ width: 12, height: 12 }} />
      </button>
    </div>
  );
}

function YearSelector({ year, onChange }) {
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 10 }, (_, i) => currentYear - 5 + i);
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
      <label style={{ ...LABEL_STYLE, margin: 0 }}>Year</label>
      <select value={year} onChange={e => onChange(parseInt(e.target.value))}
        style={{ ...SELECT_STYLE, width: 'auto', maxWidth: '120px', padding: '5px 10px' }}>
        {years.map(y => <option key={y} value={y}>{y}</option>)}
      </select>
    </div>
  );
}

// ── Inline Editable Cell ─────────────────────────────────────────────
function EditableCell({ value, onChange, type, options, align }) {
  const [local, setLocal] = useState(value ?? '');
  useEffect(() => { setLocal(value ?? ''); }, [value]);
  const commit = () => {
    const trimmed = typeof local === 'string' ? local.trim() : local;
    if (trimmed !== (value ?? '')) onChange(trimmed);
  };
  if (options) {
    return (
      <select value={local}
        onChange={e => { setLocal(e.target.value); onChange(e.target.value); }}
        style={{ ...INPUT_STYLE, padding: '4px 6px', fontSize: '12px', textAlign: align || 'left' }}>
        <option value="">—</option>
        {options.map(o => (
          <option key={typeof o === 'string' ? o : o.value} value={typeof o === 'string' ? o : o.value}>
            {typeof o === 'string' ? o : o.label}
          </option>
        ))}
      </select>
    );
  }
  return (
    <input type={type || 'text'} value={local}
      onChange={e => setLocal(e.target.value)}
      onBlur={commit}
      onKeyDown={e => { if (e.key === 'Enter') e.target.blur(); }}
      style={{ ...INPUT_STYLE, padding: '4px 6px', fontSize: '12px', textAlign: align || 'left' }}
    />
  );
}

// ── Editable Data Table ──────────────────────────────────────────────
function EditableTable({ columns, rows, onAdd, onUpdate, onDelete, editable, emptyText }) {
  return (
    <div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px', fontFamily: BRIO.font }}>
          <thead>
            <tr style={{ borderBottom: `2px solid ${BRIO.border}` }}>
              {columns.map(col => (
                <th key={col.key} style={{
                  padding: '8px 10px', textAlign: col.align || 'left',
                  fontWeight: 500, fontSize: '10px', textTransform: 'uppercase',
                  letterSpacing: '0.04em', color: BRIO.grey, whiteSpace: 'nowrap',
                }}>{col.label}</th>
              ))}
              {editable && onDelete && <th style={{ width: '40px' }}></th>}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={columns.length + (editable && onDelete ? 1 : 0)} style={{
                  padding: '20px 10px', textAlign: 'center', color: BRIO.greyMuted,
                  fontStyle: 'italic', fontSize: '12px',
                }}>{emptyText || 'No entries yet'}</td>
              </tr>
            ) : rows.map((row, idx) => (
              <tr key={row.id || idx} style={{ borderBottom: `1px solid ${BRIO.border}` }}>
                {columns.map(col => (
                  <td key={col.key} style={{
                    padding: editable && onUpdate ? '4px 4px' : '8px 10px',
                    verticalAlign: 'top', textAlign: col.align || 'left', color: BRIO.black,
                  }}>
                    {editable && onUpdate && !col.readOnly ? (
                      <EditableCell
                        value={row[col.key]} onChange={(val) => onUpdate(row.id, { [col.key]: val })}
                        type={col.inputType} options={col.options} align={col.align}
                      />
                    ) : (
                      col.render ? col.render(row[col.key], row) : (row[col.key] || '—')
                    )}
                  </td>
                ))}
                {editable && onDelete && (
                  <td style={{ padding: '6px 4px', textAlign: 'center', whiteSpace: 'nowrap' }}>
                    <button onClick={() => onDelete(row.id)}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: BRIO.greyMuted, padding: '4px' }}
                      title="Delete">
                      <Trash2 style={{ width: 13, height: 13 }} />
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {editable && onAdd && (
        <button onClick={onAdd}
          style={{
            display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 12px', marginTop: '8px',
            background: 'transparent', border: `1px dashed ${BRIO.border}`, borderRadius: 0,
            color: BRIO.gold, fontSize: '12px', fontFamily: BRIO.font, cursor: 'pointer', fontWeight: 400,
          }}>
          <Plus style={{ width: 13, height: 13 }} /> add entry
        </button>
      )}
    </div>
  );
}

// ── Address Autocomplete Search (fix #2) ─────────────────────────────
function AddressAutocomplete({ searchFn, onSelect }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const timerRef = useRef(null);
  const wrapRef = useRef(null);

  const handleSearch = (val) => {
    setQuery(val);
    clearTimeout(timerRef.current);
    if (val.length < 2) { setResults([]); setOpen(false); return; }
    timerRef.current = setTimeout(async () => {
      const r = await searchFn(val);
      setResults(r);
      setOpen(r.length > 0);
    }, 300);
  };

  useEffect(() => {
    const handleClick = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  return (
    <div ref={wrapRef} style={{ position: 'relative', marginBottom: '10px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
        <Search style={{ width: 13, height: 13, color: BRIO.greyMuted, flexShrink: 0 }} />
        <input type="text" value={query} onChange={e => handleSearch(e.target.value)}
          placeholder="Search address directory…"
          style={{ ...INPUT_STYLE, fontSize: '12px' }} />
      </div>
      {open && results.length > 0 && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 100,
          background: BRIO.white, border: `1px solid ${BRIO.border}`,
          borderRadius: 0, boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          maxHeight: '200px', overflowY: 'auto',
        }}>
          {results.map(r => (
            <button key={r.id}
              onClick={() => { onSelect(r); setQuery(''); setOpen(false); }}
              style={{
                display: 'block', width: '100%', textAlign: 'left', padding: '8px 12px',
                background: 'transparent', border: 'none', cursor: 'pointer',
                fontSize: '12px', fontFamily: BRIO.font, color: BRIO.black,
                borderBottom: `1px solid ${BRIO.border}`,
              }}>
              <div style={{ fontWeight: 500 }}>{r.label || r.line1}</div>
              <div style={{ fontSize: '11px', color: BRIO.grey }}>
                {[r.line1, r.city, r.state, r.country].filter(Boolean).join(', ')}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Inline Review/Evaluation Card ────────────────────────────────────
function InlineEditCard({ item, fields, onUpdate, onDelete, editable }) {
  const [local, setLocal] = useState({ ...item });
  useEffect(() => { setLocal({ ...item }); }, [item]);

  const handleBlur = (key) => {
    if (local[key] !== item[key]) onUpdate(item.id, { [key]: local[key] });
  };

  return (
    <div style={{
      border: `1px solid ${BRIO.border}`, borderRadius: 0, padding: '14px 16px',
      marginBottom: '10px', background: BRIO.white,
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
        <span style={{ fontSize: '11px', color: BRIO.greyMuted }}>
          {item.reviewDate || item.evaluationDate || 'No date'}
        </span>
        {editable && onDelete && (
          <button onClick={() => onDelete(item.id)}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: BRIO.greyMuted, padding: '2px' }}
            title="Delete entry">
            <Trash2 style={{ width: 12, height: 12 }} />
          </button>
        )}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px 16px' }}>
        {fields.map(f => (
          <div key={f.key} style={{ gridColumn: f.span2 ? 'span 2' : undefined }}>
            <label style={{ ...LABEL_STYLE, fontSize: '10px' }}>{f.label}</label>
            {f.type === 'textarea' ? (
              <AutoResizeTextarea
                value={local[f.key] || ''}
                onChange={e => setLocal(prev => ({ ...prev, [f.key]: e.target.value }))}
                onBlur={() => handleBlur(f.key)}
                disabled={!editable}
                minRows={3}
              />
            ) : (
              <input
                type={f.type || 'text'}
                value={local[f.key] ?? ''}
                onChange={e => setLocal(prev => ({ ...prev, [f.key]: e.target.value }))}
                onBlur={() => handleBlur(f.key)}
                disabled={!editable}
                style={INPUT_STYLE}
                step={f.type === 'number' ? '0.1' : undefined}
              />
            )}
          </div>
        ))}
      </div>
    </div>
  );
}


// ============================================================================
// MAIN COMPONENT
// ============================================================================
export default function TeamMemberFullScreenPage({
  userId, onClose, currentUser, memberUser, supabaseUsers, activeTeams,
  activeTimeEntries, activeProjects, getInternalCostRate,
  supervisorIsInMembersTeam,
}) {
  // ── Supabase profile hook ─────────────────────────────────────────
  const profileHook = useTeamMemberProfile(userId);

  // ── Role History hook (read-only here; edited via Edit Team Member modal)
  const roleHistoryHook = useUserRoleHistory(userId);

  // ── Expertise options hook ───────────────────────────────────────
  const expertiseHook = useExpertiseOptions();

  // ── Salary Ledger hook (same source as SalaryCostPage) ─────────
  const salaryLedger = useSalaryLedger();

  // ── Local state ───────────────────────────────────────────────────
  const [openDrawers, setOpenDrawers] = useState({ personal: true });
  const [personalTab, setPersonalTab] = useState('core');
  const [salaryTab, setSalaryTab] = useState('history');
  const [performanceTab, setPerformanceTab] = useState('analytics');
  const [evaluationTab, setEvaluationTab] = useState('entries');
  const [salaryYear, setSalaryYear] = useState(new Date().getFullYear());
  const [perfYear, setPerfYear] = useState(new Date().getFullYear());
  const [evalYear, setEvalYear] = useState(new Date().getFullYear());
  const [toast, setToast] = useState(null);
  const [saving, setSaving] = useState(false);

  // ── Ledger data for salary drawer ──────────────────────────────────
  const [ledgerRows, setLedgerRows] = useState([]);
  const [ledgerLoading, setLedgerLoading] = useState(false);

  // ── Draft state for editable profile fields ────────────────────────
  const [draft, setDraft] = useState({});
  const [draftDirty, setDraftDirty] = useState(false);
  const [deleteConfirmName, setDeleteConfirmName] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Initialize draft from profile when loaded
  useEffect(() => {
    if (profileHook.profile) {
      setDraft({ ...profileHook.profile });
      setDraftDirty(false);
    }
  }, [profileHook.profile]);

  // ── Permissions ────────────────────────────────────────────────────
  const viewerRole = currentUser?.role || 'user';
  const isAdmin = viewerRole === 'admin';
  const isAdminPA = viewerRole === 'admin-pa';
  const isAdminAny = isAdmin || isAdminPA;
  const isSupervisor = viewerRole === 'supervisor';
  const editable = canEdit(viewerRole);
  const deletable = canDelete(viewerRole);

  // ── Reviewer list (supervisors + admins) for review/evaluation dropdowns ──
  const reviewerUsers = (supabaseUsers?.users || []).filter(u =>
    u.role === 'admin' || u.role === 'admin-pa' || u.role === 'supervisor'
  );

  // ── Supervisor team-membership check ──────────────────────────────
  // Pre-computed in TeamMembersPage using normalizedIncludes before the overlay
  // is opened, so ID type mismatches are handled by the canonical helper.
  // True only when the viewer is a supervisor AND the member belongs to one of
  // the supervisor's teams.  Gates the Employee Evaluation Report drawer.
  const isInSupervisorsTeam = isSupervisor && !!supervisorIsInMembersTeam;

  // Supervisors have full CRUD on evaluation reports for their own team members.
  const evalEditable = editable || isInSupervisorsTeam;

  // ── Drawer toggle ──────────────────────────────────────────────────
  const toggleDrawer = useCallback((key) => {
    setOpenDrawers(prev => ({ ...prev, [key]: !prev[key] }));
  }, []);

  // ── Lock body scroll ──────────────────────────────────────────────
  useEffect(() => {
    const orig = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = orig; };
  }, []);

  // ── Toast helper ───────────────────────────────────────────────────
  const showToast = useCallback((message, type = 'success') => {
    setToast({ message, type });
    if (type === 'success') setTimeout(() => setToast(null), 3000);
  }, []);

  // ── Save profile draft ─────────────────────────────────────────────
  const handleSaveProfile = async () => {
    setSaving(true);
    const result = await profileHook.upsertProfile(draft);

    // ── Also persist bonus fields (stored on users.bonuses, not user_profiles) ──
    const bonusKeyPattern = /^bonus(Q[1-4])_(\d{4})$/;
    const bonusByYear = {};
    Object.entries(draft).forEach(([key, val]) => {
      const m = key.match(bonusKeyPattern);
      if (m) {
        const quarter = m[1].toLowerCase(); // q1..q4
        const year = m[2];
        if (!bonusByYear[year]) bonusByYear[year] = {};
        bonusByYear[year][quarter] = parseFloat(val) || 0;
      }
    });
    if (Object.keys(bonusByYear).length > 0 && supabaseUsers) {
      const existingBonuses = memberUser?.bonuses || {};
      const mergedBonuses = { ...existingBonuses };
      Object.entries(bonusByYear).forEach(([year, quarters]) => {
        mergedBonuses[year] = { ...(existingBonuses[year] || {}), ...quarters };
      });
      const bonusResult = await supabaseUsers.updateUser(userId, { bonuses: mergedBonuses });
      if (!bonusResult.success) {
        setSaving(false);
        showToast(bonusResult.error || 'Failed to save bonuses', 'error');
        return;
      }
    }

    setSaving(false);
    if (result.success) {
      setDraftDirty(false);
      showToast('Profile saved');
    } else {
      showToast(result.error || 'Save failed', 'error');
    }
  };

  // ── Update draft field (always marks dirty) ────────────────────────
  const setField = (key, value) => {
    setDraft(prev => ({ ...prev, [key]: value }));
    setDraftDirty(true);
  };

  // ── Admin actions ──────────────────────────────────────────────────
  const handleResetPassword = async () => {
    if (!memberUser?.email) { showToast('No email found', 'error'); return; }
    const authId = memberUser.authUserId || memberUser.auth_user_id;
    if (!authId) { showToast('User has no login linked', 'error'); return; }
    try {
      const mode = memberUser.status === 'invited' ? 'invite' : 'recovery';
      const result = await adminResetPassword({ email: memberUser.email, mode });
      if (result.success) showToast('Reset email sent to ' + memberUser.email);
      else showToast(result.error || 'Failed', 'error');
    } catch (err) { showToast(err.message || 'Failed', 'error'); }
  };

  const handleDeleteUser = async () => {
    if (!deletable) return;
    if (deleteConfirmName.trim().toLowerCase() !== (memberUser?.name || '').trim().toLowerCase()) return;
    setShowDeleteConfirm(false);
    setDeleteConfirmName('');
    if (supabaseUsers?.deleteUser) {
      const result = await supabaseUsers.deleteUser(userId);
      if (result.success) { showToast('User deleted'); setTimeout(() => onClose(), 800); }
      else showToast(result.error || 'Delete failed', 'error');
    }
  };

  // ── Address handlers ───────────────────────────────────────────────
  const handleAddAddress = async () => {
    const result = await profileHook.createAddress({
      label: 'New Address', line1: '', line2: '', city: '', state: '', postalCode: '', country: '',
    });
    if (result.success) showToast('Address added');
    else showToast(result.error, 'error');
  };
  const handleUpdateAddress = async (id, changes) => {
    const result = await profileHook.updateAddress(id, changes);
    if (result.success) showToast('Address saved');
    else showToast(result.error, 'error');
  };
  const handleDeleteAddress = async (id) => {
    const result = await profileHook.deleteAddress(id);
    if (result.success) showToast('Address removed');
    else showToast(result.error, 'error');
  };
  // Fill from directory pick (fix #2)
  const handleAddressFromDirectory = async (suggestion) => {
    const result = await profileHook.createAddress({
      label: suggestion.label || 'Address', line1: suggestion.line1, line2: suggestion.line2,
      city: suggestion.city, state: suggestion.state,
      postalCode: suggestion.postalCode, country: suggestion.country,
    });
    if (result.success) showToast('Address added from directory');
    else showToast(result.error, 'error');
  };

  // ── Education handlers ─────────────────────────────────────────────
  const handleAddEducation = async () => {
    const result = await profileHook.createEducation({
      type: 'bachelor', institution: '', graduationDate: null, duration: '', notes: '',
    });
    if (result.success) showToast('Education entry added');
    else showToast(result.error, 'error');
  };
  const handleUpdateEducation = async (id, changes) => {
    const result = await profileHook.updateEducation(id, changes);
    if (result.success) showToast('Education saved');
    else showToast(result.error, 'error');
  };
  const handleDeleteEducation = async (id) => {
    const result = await profileHook.deleteEducation(id);
    if (result.success) showToast('Education entry removed');
    else showToast(result.error, 'error');
  };

  // ── Employer handlers ──────────────────────────────────────────────
  const handleAddEmployer = async () => {
    const result = await profileHook.createEmployer({
      name: '', startDate: null, endDate: null, role: '', notes: '',
    });
    if (result.success) showToast('Employer added');
    else showToast(result.error, 'error');
  };
  const handleUpdateEmployer = async (id, changes) => {
    const result = await profileHook.updateEmployer(id, changes);
    if (result.success) showToast('Employer saved');
    else showToast(result.error, 'error');
  };
  const handleDeleteEmployer = async (id) => {
    const result = await profileHook.deleteEmployer(id);
    if (result.success) showToast('Employer removed');
    else showToast(result.error, 'error');
  };

  // ── Performance Review handlers (fix #8) ──────────────────────────
  const handleAddReview = async () => {
    const today = new Date().toISOString().split('T')[0];
    const result = await profileHook.createPerformanceReview({
      reviewDate: today, year: perfYear, reviewType: '', overallRating: null, confidence: '',
      discussionPoints: '', futureCriteria: '', criteria: null, problemSolving: null,
      sectionC: {}, sectionD: {}, sectionE: {}, sectionF: {},
    });
    if (result.success) showToast('Review entry created');
    else showToast(result.error, 'error');
  };
  const handleUpdateReview = async (id, changes) => {
    const result = await profileHook.updatePerformanceReview(id, changes);
    if (!result.success) showToast(result.error, 'error');
  };
  const handleDeleteReview = async (id) => {
    if (!window.confirm('Delete this review entry?')) return;
    const result = await profileHook.deletePerformanceReview(id);
    if (!result.success) showToast(result.error, 'error');
  };

  // ── Evaluation Report handlers (role-based criteria from role history) ──
  const handleAddEvaluation = async () => {
    const today = new Date().toISOString().split('T')[0];
    // Use role at today's date for initial criteria
    const roleKey = getEvalRoleForDate(today);
    const template = roleKey ? ROLE_EVALUATION_CRITERIA[roleKey] : null;
    // Build initial criteria object: { "Category Name": { "criterion text": null } }
    const initialCriteria = {};
    if (template) {
      template.forEach(cat => {
        const catRatings = {};
        cat.items.forEach(item => { catRatings[item] = null; });
        initialCriteria[cat.category] = catRatings;
      });
    }
    const result = await profileHook.createEvaluationReport({
      evaluationDate: today, year: evalYear,
      performanceRating: null, notes: '',
      criteria: Object.keys(initialCriteria).length > 0 ? initialCriteria : null,
    });
    if (result.success) showToast('Evaluation entry created');
    else showToast(result.error, 'error');
  };
  const handleUpdateEvaluation = async (id, changes) => {
    if (changes.performanceRating !== undefined) {
      changes.performanceRating = changes.performanceRating !== '' ? Number(changes.performanceRating) : null;
    }
    const result = await profileHook.updateEvaluationReport(id, changes);
    if (!result.success) showToast(result.error, 'error');
  };
  const handleDeleteEvaluation = async (id) => {
    if (!window.confirm('Delete this evaluation entry?')) return;
    const result = await profileHook.deleteEvaluationReport(id);
    if (!result.success) showToast(result.error, 'error');
  };

  // ── Total Experience calculation (fix #6) ──────────────────────────
  // Formula: years since bachelor graduation − gap duration
  const totalExperience = useMemo(() => {
    const bachelorGrad = profileHook.education.find(e => e.type === 'bachelor')?.graduationDate;
    if (!bachelorGrad) return null;
    const start = new Date(bachelorGrad);
    const now = new Date();
    const rawYears = (now - start) / (365.25 * 24 * 60 * 60 * 1000);
    const gap = Number(draft.experienceGapYears) || 0;
    return Math.max(0, rawYears - gap).toFixed(1);
  }, [draft.experienceGapYears, profileHook.education]);

  // ── Fetch ledger data when year changes ──────────────────────────
  useEffect(() => {
    const authUserId = memberUser?.authUserId || memberUser?.auth_user_id;
    if (!authUserId) return;
    const fetchLedger = async () => {
      setLedgerLoading(true);
      try {
        const fromMonth = `${salaryYear}-01-01`;
        const toMonth = `${salaryYear}-12-01`;
        const rows = await salaryLedger.getLedgerRange(authUserId, fromMonth, toMonth);
        setLedgerRows(rows || []);
      } catch (err) {
        console.warn('[FullScreen] Ledger fetch error:', err);
        setLedgerRows([]);
      } finally {
        setLedgerLoading(false);
      }
    };
    fetchLedger();
  }, [salaryYear, memberUser, salaryLedger.getLedgerRange]);

  // ── Helper: get role at a given date from role history ─────────────
  const getRoleAtDate = useCallback((dateStr) => {
    if (!dateStr || !roleHistoryHook.roleHistory.length) return null;
    // Sort ascending by effectiveFrom
    const sorted = [...roleHistoryHook.roleHistory].sort((a, b) =>
      a.effectiveFrom.localeCompare(b.effectiveFrom)
    );
    // Find the row where effectiveFrom <= date and (effectiveTo >= date OR effectiveTo is null)
    for (let i = sorted.length - 1; i >= 0; i--) {
      const row = sorted[i];
      if (row.effectiveFrom <= dateStr && (!row.effectiveTo || row.effectiveTo >= dateStr)) {
        return row;
      }
    }
    // Fallback: latest row before date
    for (let i = sorted.length - 1; i >= 0; i--) {
      if (sorted[i].effectiveFrom <= dateStr) return sorted[i];
    }
    return null;
  }, [roleHistoryHook.roleHistory]);

  // ── Evaluation role resolver (must be after getRoleAtDate) ─────────
  const fallbackJobRole = draft.currentJobRole || memberUser?.jobRole || null;
  const getEvalRoleForDate = useCallback((dateStr) => {
    const roleRow = getRoleAtDate(dateStr);
    if (roleRow) return getEvalRoleKey(roleRow.jobRole);
    return getEvalRoleKey(fallbackJobRole);
  }, [getRoleAtDate, fallbackJobRole]);

  // ── Salary entries for year: merge ledger + salary matrix + role history ──
  // Pre-2026: ledger rows as-is (no matrix fallback); if no ledger, use user_salary_history.
  // Post-2026: ledger first, then salary matrix fallback (mirrors SalaryCostPage).
  // Employment dates are used to exclude months before joining or after leaving.
  const salaryEntriesForYear = useMemo(() => {
    const SALARY_CUTOVER_YEAR = 2026;
    const isPreCutover = salaryYear < SALARY_CUTOVER_YEAR;

    // ── Employment date helpers (mirrors SalaryCostPage.resolveEmploymentDates) ──
    const parseEmpDate = (val) => {
      if (!val) return null;
      const m = String(val).match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
      if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
      const d = new Date(val);
      return isNaN(d.getTime()) ? null : d;
    };
    const empStart = parseEmpDate(memberUser?.joinDate || memberUser?.joiningDate);
    const empEnd   = parseEmpDate(memberUser?.endDate  || memberUser?.leavingDate);

    // Returns true when the employee was employed during the given month.
    // If start date is unknown, include the month (fail open — incomplete data).
    const isEmployedInMonth = (year, month) => {
      if (!empStart) return true;
      const monthStart = new Date(year, month - 1, 1);
      const monthEnd   = new Date(year, month, 0); // last day of month
      return empStart <= monthEnd && (!empEnd || empEnd >= monthStart);
    };

    if (ledgerRows.length > 0) {
      // Build entries from ledger, deriving role/level from role history.
      // Filter out any rows outside the employment window (defensive — the
      // server-side rebuild should already enforce this, but protect here too).
      return ledgerRows
        .filter(row => {
          const monthNum = parseInt(row.month.substring(5, 7), 10);
          const yearNum  = parseInt(row.month.substring(0, 4), 10);
          return isEmployedInMonth(yearNum, monthNum);
        })
        .map(row => {
          const monthDate = row.month; // YYYY-MM-01
          const monthNum  = parseInt(monthDate.substring(5, 7), 10);
          const roleAtMonth = getRoleAtDate(monthDate);
          let salary = row.base_salary || 0;
          let bonus  = row.bonus || 0;
          let source = row.source === 'legacy_import' ? 'legacy' : (row.source || 'derived');

          // Post-2026 only: if ledger row has zero salary, fall back to salary matrix.
          // Pre-2026: trust the ledger (or show zero) — never use the matrix.
          if (!isPreCutover && salary === 0 && bonus === 0 && getInternalCostRate && memberUser?.id) {
            const internal = getInternalCostRate(memberUser.id, salaryYear, monthNum);
            if (internal && (internal.monthlySalary > 0 || internal.monthlyBonusEquivalent > 0)) {
              salary = internal.monthlySalary || 0;
              bonus  = internal.monthlyBonusEquivalent || 0;
              source = internal.source || 'salary_matrix';
            }
          }

          return {
            id: row.id || monthDate,
            effectiveDate: monthDate,
            salary,
            bonus,
            jobRole: roleAtMonth?.jobRole || '',
            jobLevel: roleAtMonth?.jobLevel ?? null,
            source,
            reason: '',
            changedBy: '',
          };
        });
    }

    // No ledger rows for pre-2026: use user_salary_history records for this year.
    if (isPreCutover) {
      return profileHook.salaryHistory.filter(s => {
        if (!s.effectiveDate) return false;
        return new Date(s.effectiveDate).getFullYear() === salaryYear;
      });
    }

    // No ledger rows for post-2026: try salary matrix for each employed month of the year.
    if (getInternalCostRate && memberUser?.id) {
      const entries = [];
      for (let m = 1; m <= 12; m++) {
        if (!isEmployedInMonth(salaryYear, m)) continue; // skip non-employment months
        const internal = getInternalCostRate(memberUser.id, salaryYear, m);
        if (internal && (internal.monthlySalary > 0 || internal.monthlyBonusEquivalent > 0)) {
          const monthStr    = `${salaryYear}-${String(m).padStart(2, '0')}-01`;
          const roleAtMonth = getRoleAtDate(monthStr);
          entries.push({
            id: monthStr,
            effectiveDate: monthStr,
            salary: internal.monthlySalary || 0,
            bonus:  internal.monthlyBonusEquivalent || 0,
            jobRole:   roleAtMonth?.jobRole || '',
            jobLevel:  roleAtMonth?.jobLevel ?? null,
            source:    internal.source || 'salary_matrix',
            reason:    '',
            changedBy: '',
          });
        }
      }
      if (entries.length > 0) return entries;
    }

    return [];
  }, [ledgerRows, profileHook.salaryHistory, salaryYear, getRoleAtDate, getInternalCostRate, memberUser]);

  // ── Ledger-derived quarterly bonus for selected year ──────────────
  // Falls back to salary_monthly_ledger data when users.bonuses[year] is not set
  // or all-zero (mirrors how SalaryCostPage resolves bonus for pre-cutover years).
  const ledgerBonusByQuarter = useMemo(() => {
    const yearBonuses = memberUser?.bonuses?.[salaryYear];
    const hasManualBonuses = yearBonuses &&
      ((yearBonuses.q1 || 0) + (yearBonuses.q2 || 0) +
       (yearBonuses.q3 || 0) + (yearBonuses.q4 || 0)) > 0;
    if (hasManualBonuses) return null; // users.bonuses has explicit data — use it

    const q = { q1: 0, q2: 0, q3: 0, q4: 0, hasData: false };
    for (const entry of salaryEntriesForYear) {
      const bonus = entry.bonus || 0;
      if (bonus === 0) continue;
      q.hasData = true;
      const month = parseInt(entry.effectiveDate.substring(5, 7), 10);
      if (month <= 3) q.q1 += bonus;
      else if (month <= 6) q.q2 += bonus;
      else if (month <= 9) q.q3 += bonus;
      else q.q4 += bonus;
    }
    return q.hasData ? q : null;
  }, [memberUser, salaryYear, salaryEntriesForYear]);

  // ── Reviews filtered by year ───────────────────────────────────────
  const reviewsForYear = useMemo(() => {
    return profileHook.performanceReviews.filter(r => r.year === perfYear);
  }, [profileHook.performanceReviews, perfYear]);

  // ── Evaluations filtered by year ───────────────────────────────────
  const evaluationsForYear = useMemo(() => {
    return profileHook.evaluationReports.filter(r => r.year === evalYear);
  }, [profileHook.evaluationReports, evalYear]);

  // ════════════════════════════════════════════════════════════════════
  // RENDER
  // ════════════════════════════════════════════════════════════════════
  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 10000, background: BRIO.greyLight,
      display: 'flex', flexDirection: 'column', fontFamily: BRIO.font,
    }}>
      {/* ══════════════════════════════════════════════════════════════
          STICKY HEADER
          ══════════════════════════════════════════════════════════════ */}
      <header style={{
        position: 'sticky', top: 0, zIndex: 10001, background: BRIO.white,
        borderBottom: `1px solid ${BRIO.border}`, padding: '12px 24px',
        display: 'flex', alignItems: 'center', gap: '16px', flexShrink: 0,
        boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
      }}>
        {/* Close */}
        <button onClick={onClose}
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            width: 32, height: 32, background: 'transparent',
            border: `1px solid ${BRIO.border}`, borderRadius: 0,
            cursor: 'pointer', color: BRIO.grey, flexShrink: 0,
          }} title="Close">
          <X style={{ width: 16, height: 16 }} />
        </button>

        {/* Name + badges */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
            <h1 style={{
              margin: 0, fontSize: '18px', fontWeight: 500, color: BRIO.black,
              fontFamily: BRIO.font, textTransform: 'lowercase',
              whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
            }}>{memberUser?.name || 'Team Member'}</h1>
            <RoleBadge role={memberUser?.role || 'user'} />
            <StatusBadge isActive={memberUser?.isActive !== false} />
          </div>
          <div style={{ fontSize: '11px', color: BRIO.grey, marginTop: '2px' }}>
            {memberUser?.email || ''}{memberUser?.officeLocation ? ` · ${memberUser.officeLocation}` : ''}
          </div>
        </div>

        {/* Header action buttons */}
        <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
          {/* FIX #1: Save always visible for editable users */}
          {editable && (
            <button onClick={handleSaveProfile}
              disabled={!draftDirty || saving}
              title={!draftDirty
                ? 'No unsaved changes — table edits auto-save on blur'
                : 'Save profile changes (personal details, gap, IDs)'}
              style={{
                display: 'flex', alignItems: 'center', gap: '6px',
                padding: '6px 14px',
                background: draftDirty ? BRIO.gold : BRIO.greyLight,
                color: draftDirty ? BRIO.white : BRIO.greyMuted,
                border: draftDirty ? 'none' : `1px solid ${BRIO.border}`,
                borderRadius: 0, fontSize: '12px', fontWeight: 500, fontFamily: BRIO.font,
                cursor: draftDirty ? 'pointer' : 'default',
                opacity: saving ? 0.6 : 1,
                transition: 'all 0.2s',
              }}>
              {saving
                ? <Loader2 style={{ width: 13, height: 13, animation: 'spin 1s linear infinite' }} />
                : <Save style={{ width: 13, height: 13 }} />}
              {saving ? 'Saving…' : 'Save Changes'}
            </button>
          )}

          {isAdmin && (
            <button onClick={handleResetPassword}
              style={{
                display: 'flex', alignItems: 'center', gap: '5px', padding: '6px 12px',
                background: 'transparent', border: `1px solid ${BRIO.border}`, borderRadius: 0,
                color: BRIO.grey, fontSize: '11px', fontFamily: BRIO.font, cursor: 'pointer',
              }} title="Reset password / Resend invite">
              <Key style={{ width: 12, height: 12 }} />reset password
            </button>
          )}

          {deletable && !showDeleteConfirm && (
            <button onClick={() => { setShowDeleteConfirm(true); setDeleteConfirmName(''); }}
              style={{
                display: 'flex', alignItems: 'center', gap: '5px', padding: '6px 12px',
                background: 'transparent', border: `1px solid ${BRIO.danger}33`, borderRadius: 0,
                color: BRIO.danger, fontSize: '11px', fontFamily: BRIO.font, cursor: 'pointer',
              }} title="Delete user permanently">
              <Trash2 style={{ width: 12, height: 12 }} />delete
            </button>
          )}
          {deletable && showDeleteConfirm && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <input
                autoFocus
                type="text"
                placeholder={`Type "${memberUser?.name}" to confirm`}
                value={deleteConfirmName}
                onChange={e => setDeleteConfirmName(e.target.value)}
                onKeyDown={e => { if (e.key === 'Escape') { setShowDeleteConfirm(false); setDeleteConfirmName(''); } }}
                style={{
                  padding: '4px 8px', fontSize: '11px', fontFamily: BRIO.font,
                  border: `1px solid ${BRIO.danger}66`, borderRadius: 0, width: '180px',
                  outline: 'none',
                }}
              />
              <button onClick={handleDeleteUser}
                disabled={deleteConfirmName.trim().toLowerCase() !== (memberUser?.name || '').trim().toLowerCase()}
                style={{
                  padding: '4px 10px', fontSize: '11px', fontFamily: BRIO.font,
                  background: deleteConfirmName.trim().toLowerCase() === (memberUser?.name || '').trim().toLowerCase() ? BRIO.danger : `${BRIO.danger}33`,
                  color: '#fff', border: 'none', borderRadius: 0, cursor: 'pointer',
                  opacity: deleteConfirmName.trim().toLowerCase() === (memberUser?.name || '').trim().toLowerCase() ? 1 : 0.5,
                }}>
                Confirm
              </button>
              <button onClick={() => { setShowDeleteConfirm(false); setDeleteConfirmName(''); }}
                style={{
                  padding: '4px 8px', fontSize: '11px', fontFamily: BRIO.font,
                  background: 'transparent', border: `1px solid ${BRIO.border}`, borderRadius: 0,
                  color: BRIO.grey, cursor: 'pointer',
                }}>
                Cancel
              </button>
            </div>
          )}
        </div>
      </header>

      {/* ══════════════════════════════════════════════════════════════
          SCROLLABLE BODY
          ══════════════════════════════════════════════════════════════ */}
      <div style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden' }}>
        {profileHook.loading && (
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: '48px', color: BRIO.grey, gap: '8px',
          }}>
            <Loader2 style={{ width: 18, height: 18, animation: 'spin 1s linear infinite' }} />
            <span style={{ fontSize: '13px', fontFamily: BRIO.font }}>Loading profile…</span>
          </div>
        )}

        <div style={{ maxWidth: '900px', margin: '0 auto', padding: '16px 24px 48px' }}>

          {/* ════════════════════════════════════════════════════════
              DRAWER 1: PERSONAL DETAILS
              ════════════════════════════════════════════════════════ */}
          {(isAdminAny || isSupervisor) && (
            <Drawer title="Personal Details" icon={MapPin}
              open={openDrawers.personal} onToggle={() => toggleDrawer('personal')}>
              <TabBar
                tabs={[
                  { id: 'core', label: 'Core' },
                  { id: 'contacts', label: 'Contacts' },
                  { id: 'ids', label: 'IDs & Docs' },
                ]}
                active={personalTab} onChange={setPersonalTab}
              />

              {/* ── Core Tab ──────────────────────────────────────── */}
              {personalTab === 'core' && (
                <FieldGrid cols={2}>
                  <Field label="Designation">
                    <select value={draft.designation || ''} onChange={e => setField('designation', e.target.value)}
                      disabled={!editable} style={SELECT_STYLE}>
                      <option value="">—</option>
                      <option value="employee">Employee</option>
                      <option value="consultant">Consultant</option>
                    </select>
                  </Field>
                  <Field label="Office Location">
                    <select value={draft.officeLocation || 'NL'} onChange={e => setField('officeLocation', e.target.value)}
                      disabled={!editable} style={SELECT_STYLE}>
                      <option value="NL">NL</option>
                      <option value="IN">IN</option>
                    </select>
                  </Field>
                  <Field label="Current Job Role">
                    <div style={{ padding: '7px 10px', fontSize: '13px', color: 'var(--brio-text, #1e293b)', background: 'var(--brio-bg-subtle, #fafaf6)', borderRadius: 0, border: '1px solid var(--brio-border, #e2e8f0)' }}>
                      {draft.currentJobRole || memberUser?.jobRole || <span style={{ color: 'var(--brio-grey)', fontStyle: 'italic' }}>Not set</span>}
                      <div style={{ fontSize: '10px', color: 'var(--brio-grey)', marginTop: '2px' }}>Managed via Role History</div>
                    </div>
                  </Field>
                  <Field label="Current Job Level">
                    <div style={{ padding: '7px 10px', fontSize: '13px', color: 'var(--brio-text, #1e293b)', background: 'var(--brio-bg-subtle, #fafaf6)', borderRadius: 0, border: '1px solid var(--brio-border, #e2e8f0)', maxWidth: '100px' }}>
                      {(draft.currentJobLevel ?? memberUser?.jobLevel) !== null && (draft.currentJobLevel ?? memberUser?.jobLevel) !== undefined
                        ? `Level ${draft.currentJobLevel ?? memberUser?.jobLevel}`
                        : <span style={{ color: 'var(--brio-grey)', fontStyle: 'italic' }}>Not set</span>}
                      <div style={{ fontSize: '10px', color: 'var(--brio-grey)', marginTop: '2px' }}>Managed via Role History</div>
                    </div>
                  </Field>
                  <Field label="Joining Date">
                    <BrioDatePicker value={draft.joiningDate || memberUser?.joinDate || ''}
                      onChange={v => setField('joiningDate', v)}
                      disabled={!editable} />
                  </Field>
                  <Field label="Leaving Date">
                    <BrioDatePicker value={draft.leavingDate || ''}
                      onChange={v => setField('leavingDate', v)}
                      disabled={!editable} />
                  </Field>
                  <Field label="Date of Birth">
                    <BrioDatePicker value={draft.dob || ''}
                      onChange={v => setField('dob', v)}
                      disabled={!editable} />
                  </Field>
                  <Field label="Home City">
                    <input type="text" value={draft.homeCity || ''}
                      onChange={e => setField('homeCity', e.target.value)}
                      disabled={!editable} style={INPUT_STYLE} placeholder="e.g. Mumbai" />
                  </Field>
                  <Field label="Graduation Date">
                    <BrioDatePicker value={draft.graduationDate || ''}
                      onChange={v => setField('graduationDate', v)}
                      disabled={!editable} />
                  </Field>

                  {/* FIX #6: Total Experience (computed, read-only) */}
                  <Field label="Total Experience (yrs)">
                    <div style={{
                      padding: '7px 10px', background: BRIO.goldLight, borderRadius: 0,
                      fontSize: '13px', fontWeight: 500, color: BRIO.gold, fontFamily: BRIO.font,
                    }}>
                      {totalExperience !== null ? `${totalExperience} years` : 'Add bachelor graduation date in Resume'}
                    </div>
                    <span style={{ fontSize: '10px', color: BRIO.greyMuted, marginTop: '2px', display: 'block' }}>
                      = bachelor graduation date − gap duration
                    </span>
                  </Field>

                  <Field label="Planning Masters?">
                    <BrioMonthPicker
                      value={draft.planningMasters || ''}
                      onChange={v => setField('planningMasters', v)}
                      clearable
                      placeholder="Select month"
                      disabled={!editable}
                    />
                  </Field>

                  <Field label="Personality Test Result" span2>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                      <div style={{ flex: 1, position: 'relative' }}>
                        <input type="text" value={draft.personalityTestResult || ''}
                          onChange={e => setField('personalityTestResult', e.target.value)}
                          disabled={!editable} style={{
                            ...INPUT_STYLE,
                            ...(draft.personalityTestResult && parseMbtiType(draft.personalityTestResult) ? {
                              color: getPersonalityHex(draft.personalityTestResult),
                              fontWeight: 600
                            } : {})
                          }} placeholder="e.g. INTJ-A" />
                      </div>
                      <input type="url" value={draft.personalityTestLink || ''}
                        onChange={e => setField('personalityTestLink', e.target.value)}
                        disabled={!editable} style={{ ...INPUT_STYLE, flex: 1 }} placeholder="Link to results" />
                    </div>
                  </Field>

                  {/* Expertise multi-select */}
                  <Field label="Expertise" span2>
                    <ExpertiseMultiSelect
                      value={normalizeExpertiseArray(draft.expertise)}
                      onChange={v => setField('expertise', v)}
                      options={expertiseHook.options}
                      onAddOption={expertiseHook.addOption}
                      disabled={!editable}
                      canAddOptions={editable && (isAdminAny || isSupervisor)}
                      placeholder="Select expertise tags..."
                    />
                  </Field>

                  {/* Profile Photo Upload */}
                  <Field label="Profile Photo" span2>
                    <ProfilePhotoCrop
                      userId={userId}
                      currentPhotoRect={draft.profilePhotoRect || ''}
                      currentPhotoRound={draft.profilePhotoRound || ''}
                      disabled={!editable}
                      onSave={async (photos) => {
                        // photos contain storage paths, not base64
                        setField('profilePhotoOriginal', photos.profilePhotoOriginal);
                        setField('profilePhotoRect', photos.profilePhotoRect);
                        setField('profilePhotoRound', photos.profilePhotoRound);
                      }}
                    />
                  </Field>

                  {/* Profile Intro — expandable text block */}
                  <Field label="Profile Intro" span2>
                    {editable ? (
                      <AutoResizeTextarea
                        value={draft.profileIntro || ''}
                        onChange={e => setField('profileIntro', e.target.value)}
                        disabled={!editable}
                        placeholder="A few words about this team member…"
                        minRows={2}
                      />
                    ) : (
                      (draft.profileIntro || memberUser?.profileIntro) ? (
                        <div style={{
                          padding: '8px 10px',
                          fontSize: '13px',
                          lineHeight: '1.6',
                          color: BRIO.black,
                          fontFamily: BRIO.font,
                          border: `1px solid ${BRIO.border}`,
                          borderRadius: 0,
                          background: BRIO.greyLight,
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word',
                        }}>
                          {draft.profileIntro || memberUser?.profileIntro}
                        </div>
                      ) : null
                    )}
                  </Field>
                </FieldGrid>
              )}

              {/* ── Contacts Tab ──────────────────────────────────── */}
              {personalTab === 'contacts' && (
                <div>
                  <FieldGrid cols={2}>
                    <Field label="Mobile Phone">
                      <input type="tel" value={draft.phoneMobile || ''}
                        onChange={e => setField('phoneMobile', e.target.value)}
                        disabled={!editable} style={INPUT_STYLE} />
                    </Field>
                    <Field label="WhatsApp">
                      <input type="tel" value={draft.phoneWhatsapp || ''}
                        onChange={e => setField('phoneWhatsapp', e.target.value)}
                        disabled={!editable} style={INPUT_STYLE} />
                    </Field>
                    <Field label={draft.phoneCustomLabel || 'Custom Phone'}>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <input type="text" value={draft.phoneCustomLabel || ''}
                          onChange={e => setField('phoneCustomLabel', e.target.value)}
                          disabled={!editable} style={{ ...INPUT_STYLE, maxWidth: '100px' }} placeholder="Label" />
                        <input type="tel" value={draft.phoneCustomValue || ''}
                          onChange={e => setField('phoneCustomValue', e.target.value)}
                          disabled={!editable} style={{ ...INPUT_STYLE, flex: 1 }} placeholder="Number" />
                      </div>
                    </Field>
                    <div></div>
                    <Field label="Office Email">
                      <input type="email" value={draft.emailOffice || memberUser?.email || ''}
                        onChange={e => setField('emailOffice', e.target.value)}
                        disabled={!editable} style={INPUT_STYLE} />
                    </Field>
                    {(isAdminAny || isSupervisor) && (
                      <Field label="Personal Email">
                        <input type="email" value={draft.emailPersonal || ''}
                          onChange={e => setField('emailPersonal', e.target.value)}
                          disabled={!editable} style={INPUT_STYLE} />
                      </Field>
                    )}
                  </FieldGrid>

                  {/* Addresses with autocomplete (fix #2) */}
                  {(isAdminAny || isSupervisor) && (
                    <div style={{ marginTop: '16px', paddingTop: '12px', borderTop: `1px solid ${BRIO.border}` }}>
                      <div style={{ ...LABEL_STYLE, marginBottom: '8px' }}>Addresses</div>
                      {editable && (
                        <AddressAutocomplete
                          searchFn={profileHook.searchAddressDirectory}
                          onSelect={handleAddressFromDirectory}
                        />
                      )}
                      <EditableTable
                        columns={[
                          { key: 'label', label: 'Label' },
                          { key: 'line1', label: 'Street' },
                          { key: 'city', label: 'City' },
                          { key: 'state', label: 'State' },
                          { key: 'postalCode', label: 'Postal' },
                          { key: 'country', label: 'Country' },
                        ]}
                        rows={profileHook.addresses}
                        editable={editable}
                        onAdd={handleAddAddress}
                        onUpdate={handleUpdateAddress}
                        onDelete={handleDeleteAddress}
                        emptyText="No addresses recorded"
                      />
                    </div>
                  )}
                </div>
              )}

              {/* ── IDs & Documents Tab ───────────────────────────── */}
              {personalTab === 'ids' && isAdminAny && (
                <FieldGrid cols={2}>
                  <Field label="PAN Card">
                    <input type="text" value={draft.panCard || ''}
                      onChange={e => setField('panCard', e.target.value.toUpperCase())}
                      disabled={!editable} style={INPUT_STYLE} placeholder="ABCDE1234F" maxLength={10} />
                  </Field>
                  <div></div>
                </FieldGrid>
              )}
              {personalTab === 'ids' && !isAdminAny && (
                <div style={{ padding: '20px', textAlign: 'center', color: BRIO.greyMuted, fontSize: '13px' }}>
                  ID documents are restricted to admin access only.
                </div>
              )}
            </Drawer>
          )}

          {/* ════════════════════════════════════════════════════════
              DRAWER 2: RESUME (fix #3 rename, fix #4 vertical layout)
              ════════════════════════════════════════════════════════ */}
          {(isAdminAny || isSupervisor) && (
            <Drawer title="Resume" icon={GraduationCap}
              open={openDrawers.education} onToggle={() => toggleDrawer('education')}
              count={profileHook.education.length + profileHook.employers.length}>

              {/* ── Degrees section ──────────────────────────────── */}
              <div style={SECTION_LABEL_STYLE}>Degrees</div>
              <EditableTable
                columns={[
                  {
                    key: 'type', label: 'Type',
                    options: [
                      { value: 'bachelor', label: 'Bachelor' },
                      { value: 'master', label: 'Master' },
                      { value: 'phd', label: 'PhD' },
                      { value: 'diploma', label: 'Diploma' },
                      { value: 'certificate', label: 'Certificate' },
                      { value: 'other', label: 'Other' },
                    ],
                    render: (val) => (
                      <span style={{
                        padding: '2px 8px', background: BRIO.goldLight,
                        borderRadius: 0, fontSize: '11px', fontWeight: 500, textTransform: 'capitalize',
                      }}>{val || '—'}</span>
                    ),
                  },
                  { key: 'institution', label: 'Institution' },
                  { key: 'graduationDate', label: 'Graduation', inputType: 'date', render: (val) => val || '—' },
                  { key: 'duration', label: 'Duration' },
                  { key: 'notes', label: 'Notes' },
                ]}
                rows={profileHook.education}
                editable={editable}
                onAdd={handleAddEducation}
                onUpdate={handleUpdateEducation}
                onDelete={handleDeleteEducation}
                emptyText="No education entries"
              />

              {/* ── Employers section ────────────────────────────── */}
              <div style={SECTION_LABEL_STYLE}>Employers</div>
              <EditableTable
                columns={[
                  { key: 'name', label: 'Employer' },
                  { key: 'role', label: 'Role' },
                  { key: 'startDate', label: 'Start', inputType: 'date', render: (val) => val || '—' },
                  { key: 'endDate', label: 'End', inputType: 'date', render: (val) => val || 'present' },
                  { key: 'notes', label: 'Notes' },
                ]}
                rows={profileHook.employers}
                editable={editable}
                onAdd={handleAddEmployer}
                onUpdate={handleUpdateEmployer}
                onDelete={handleDeleteEmployer}
                emptyText="No employer entries"
              />

              {/* ── Gap section (fix #5: manual fields) ──────────── */}
              <div style={SECTION_LABEL_STYLE}>Experience Gap</div>
              <FieldGrid cols={2}>
                <Field label="Gap Duration (years)">
                  <input type="number" step="0.1" min="0"
                    value={draft.experienceGapYears ?? ''}
                    onChange={e => setField('experienceGapYears', e.target.value !== '' ? parseFloat(e.target.value) : null)}
                    disabled={!editable} style={{ ...INPUT_STYLE, maxWidth: '140px' }}
                    placeholder="e.g. 1.5" />
                </Field>
                <Field label="Total Experience (computed)">
                  <div style={{
                    padding: '7px 10px', background: BRIO.infoBg, borderRadius: 0,
                    fontSize: '13px', fontWeight: 500, color: 'var(--brio-gold)', fontFamily: BRIO.font,
                  }}>
                    {totalExperience !== null ? `${totalExperience} years` : 'Add bachelor graduation date above'}
                  </div>
                  <span style={{ fontSize: '10px', color: BRIO.greyMuted, marginTop: '2px', display: 'block' }}>
                    = bachelor graduation − gap duration
                  </span>
                </Field>
                <Field label="Gap Notes" span2>
                  <AutoResizeTextarea
                    value={draft.experienceGapNotes || ''}
                    onChange={e => setField('experienceGapNotes', e.target.value)}
                    disabled={!editable}
                    minRows={2}
                    placeholder="Reason for gap, sabbatical details, etc."
                  />
                </Field>
              </FieldGrid>
            </Drawer>
          )}

          {/* ════════════════════════════════════════════════════════
              DRAWER 3: SALARY (ledger-sourced + role history)
              ════════════════════════════════════════════════════════ */}
          {isAdminAny && (
            <Drawer title="Salary" icon={DollarSign}
              open={openDrawers.salary} onToggle={() => toggleDrawer('salary')}
              count={ledgerRows.length || profileHook.salaryHistory.length}>
              <YearSelector year={salaryYear} onChange={setSalaryYear} />
              <TabBar
                tabs={[
                  { id: 'history', label: 'History' },
                  { id: 'bonus', label: 'Bonus' },
                  { id: 'reimbursements', label: 'Reimbursements' },
                ]}
                active={salaryTab} onChange={setSalaryTab}
              />

              {salaryTab === 'history' && (
                <>
                  {/* Source indicator */}
                  <div style={{
                    padding: '6px 12px', marginBottom: '10px',
                    background: salaryEntriesForYear.length > 0 ? BRIO.successBg : BRIO.warningBg,
                    border: `1px solid ${salaryEntriesForYear.length > 0 ? 'rgba(132, 164, 140, 0.3)' : 'var(--brio-yellow)'}`,
                    borderRadius: 0, fontSize: '11px',
                    color: salaryEntriesForYear.length > 0 ? 'var(--brio-green)' : 'var(--brio-orange)',
                    fontFamily: BRIO.font,
                  }}>
                    {ledgerLoading ? 'Loading salary data…' :
                     salaryEntriesForYear.length > 0
                       ? `Showing ${salaryEntriesForYear.length} months. Role/Level derived from Role History.`
                       : 'No salary data for this year. Showing legacy salary history if available.'}
                  </div>

                  {/* Salary History table with Bonus column */}
                  <EditableTable
                    columns={[
                      { key: 'effectiveDate', label: 'Month', readOnly: true,
                        render: (val) => {
                          if (!val) return '—';
                          const d = new Date(val + 'T00:00:00');
                          const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
                          return `${months[d.getMonth()]} ${d.getFullYear()}`;
                        }},
                      { key: 'salary', label: 'Base Salary', align: 'right', readOnly: true,
                        render: (val) => val !== undefined && val !== null ? `₹${Number(val).toLocaleString()}` : '—' },
                      { key: 'bonus', label: 'Bonus', align: 'right', readOnly: true,
                        render: (val) => val ? `₹${Number(val).toLocaleString()}` : '—' },
                      { key: 'jobRole', label: 'Role', readOnly: true,
                        render: (val) => val || '—' },
                      { key: 'jobLevel', label: 'Level', readOnly: true,
                        render: (val) => val !== null && val !== undefined ? `L${val}` : '—' },
                      { key: 'source', label: 'Source', readOnly: true,
                        render: (val) => {
                          const isMatrix = val && val.startsWith('salary_matrix');
                          const color = val === 'legacy' ? 'var(--brio-gold)'
                            : val === 'derived' ? 'var(--brio-green)'
                            : isMatrix ? '#1565C0'
                            : val === 'override' ? '#F57F17'
                            : 'var(--brio-grey)';
                          const bg = val === 'legacy' ? 'var(--brio-gold-light)'
                            : val === 'derived' ? 'rgba(132, 164, 140, 0.08)'
                            : isMatrix ? '#E3F2FD'
                            : val === 'override' ? '#FFF8E1'
                            : 'var(--brio-grey-light)';
                          return (
                            <span style={{ padding: '1px 6px', borderRadius: 0, fontSize: '10px', background: bg, color }}>{val || '—'}</span>
                          );
                        }},
                    ]}
                    rows={salaryEntriesForYear}
                    editable={false}
                    emptyText={ledgerLoading ? 'Loading…' : `No salary entries for ${salaryYear}`}
                  />

                  {/* Read-only Role & Level History (from role_history) */}
                  {roleHistoryHook.roleHistory.length > 0 && (
                    <details style={{ marginTop: '16px' }}>
                      <summary style={{
                        fontSize: '12px', fontWeight: 500, color: BRIO.grey,
                        cursor: 'pointer', fontFamily: BRIO.font, padding: '4px 0',
                      }}>
                        Role & Level History ({roleHistoryHook.roleHistory.length} entries)
                      </summary>
                      <div style={{ marginTop: '8px' }}>
                        <table style={{ width: '100%', fontSize: '11px', borderCollapse: 'collapse', fontFamily: BRIO.font }}>
                          <thead>
                            <tr>
                              {['From', 'To', 'Role', 'Level', 'Office', 'Notes'].map(h => (
                                <th key={h} style={{
                                  padding: '4px 8px', textAlign: 'left', fontWeight: 500,
                                  color: BRIO.greyMuted, borderBottom: `2px solid ${BRIO.border}`,
                                  fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.03em',
                                }}>{h}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {roleHistoryHook.roleHistory.map(row => (
                              <tr key={row.id} style={{ background: row.effectiveTo === null ? 'rgba(159,144,42,0.04)' : 'transparent' }}>
                                <td style={{ padding: '4px 8px', borderBottom: `1px solid ${BRIO.border}` }}>{row.effectiveFrom}</td>
                                <td style={{ padding: '4px 8px', borderBottom: `1px solid ${BRIO.border}` }}>
                                  {row.effectiveTo || <span style={{ color: BRIO.success, fontWeight: 500, fontSize: '10px' }}>Current</span>}
                                </td>
                                <td style={{ padding: '4px 8px', borderBottom: `1px solid ${BRIO.border}` }}>{row.jobRole}</td>
                                <td style={{ padding: '4px 8px', borderBottom: `1px solid ${BRIO.border}` }}>{row.jobLevel}</td>
                                <td style={{ padding: '4px 8px', borderBottom: `1px solid ${BRIO.border}` }}>{row.officeLocation || '—'}</td>
                                <td style={{ padding: '4px 8px', borderBottom: `1px solid ${BRIO.border}`, color: BRIO.greyMuted }}>{row.notes || '—'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        <div style={{ fontSize: '10px', color: BRIO.greyMuted, marginTop: '4px', fontStyle: 'italic' }}>
                          Role history is managed in the Edit Team Member modal.
                        </div>
                      </div>
                    </details>
                  )}

                  {/* Legacy all-time fallback (only shown when no ledger or matrix data) */}
                  {ledgerRows.length === 0 && salaryEntriesForYear.length === 0 && profileHook.salaryHistory.length > 0 && (
                    <details style={{ marginTop: '12px' }}>
                      <summary style={{ fontSize: '11px', color: BRIO.grey, cursor: 'pointer', fontFamily: BRIO.font, padding: '4px 0' }}>
                        All-time salary history ({profileHook.salaryHistory.length} entries)
                      </summary>
                      <div style={{ marginTop: '8px', paddingLeft: '12px', borderLeft: `2px solid ${BRIO.border}` }}>
                        {profileHook.salaryHistory.map((s, idx) => (
                          <div key={s.id || idx} style={{ fontSize: '11px', color: BRIO.grey, padding: '3px 0', borderBottom: `1px solid ${BRIO.border}` }}>
                            <span style={{ fontWeight: 500 }}>{s.effectiveDate}</span>
                            {' · '}₹{Number(s.salary).toLocaleString()}
                            {s.jobRole && ` · ${s.jobRole}`}
                            {s.jobLevel !== null && ` L${s.jobLevel}`}
                            {s.reason && <span style={{ opacity: 0.7 }}> — {s.reason}</span>}
                          </div>
                        ))}
                      </div>
                    </details>
                  )}
                </>
              )}

              {salaryTab === 'bonus' && (
                <div style={{ padding: '16px 0' }}>
                  <FieldGrid cols={4}>
                    <Field label="Q1">
                      <input type="number" style={INPUT_STYLE} placeholder="₹" disabled={!editable}
                        value={draft[`bonusQ1_${salaryYear}`] ?? memberUser?.bonuses?.[salaryYear]?.q1 ?? ledgerBonusByQuarter?.q1 ?? ''}
                        onChange={e => setField(`bonusQ1_${salaryYear}`, e.target.value)} />
                    </Field>
                    <Field label="Q2">
                      <input type="number" style={INPUT_STYLE} placeholder="₹" disabled={!editable}
                        value={draft[`bonusQ2_${salaryYear}`] ?? memberUser?.bonuses?.[salaryYear]?.q2 ?? ledgerBonusByQuarter?.q2 ?? ''}
                        onChange={e => setField(`bonusQ2_${salaryYear}`, e.target.value)} />
                    </Field>
                    <Field label="Q3">
                      <input type="number" style={INPUT_STYLE} placeholder="₹" disabled={!editable}
                        value={draft[`bonusQ3_${salaryYear}`] ?? memberUser?.bonuses?.[salaryYear]?.q3 ?? ledgerBonusByQuarter?.q3 ?? ''}
                        onChange={e => setField(`bonusQ3_${salaryYear}`, e.target.value)} />
                    </Field>
                    <Field label="Q4">
                      <input type="number" style={INPUT_STYLE} placeholder="₹" disabled={!editable}
                        value={draft[`bonusQ4_${salaryYear}`] ?? memberUser?.bonuses?.[salaryYear]?.q4 ?? ledgerBonusByQuarter?.q4 ?? ''}
                        onChange={e => setField(`bonusQ4_${salaryYear}`, e.target.value)} />
                    </Field>
                  </FieldGrid>
                  {ledgerBonusByQuarter && (
                    <div style={{ fontSize: '11px', color: BRIO.greyMuted, fontStyle: 'italic', marginTop: '8px' }}>
                      Showing bonus from salary ledger (legacy import). To override, edit the quarterly values above and save.
                    </div>
                  )}
                </div>
              )}

              {salaryTab === 'reimbursements' && (
                <div style={{ padding: '16px 0' }}>
                  <div style={{ fontSize: '12px', color: BRIO.greyMuted, fontStyle: 'italic', textAlign: 'center', padding: '24px' }}>
                    Reimbursements table (phone bills, phone, laptop, etc.) — scaffolded for future Supabase table.
                  </div>
                </div>
              )}
            </Drawer>
          )}

          {/* ════════════════════════════════════════════════════════
              DRAWER 4: PERFORMANCE REVIEWS (fix #8)
              ════════════════════════════════════════════════════════ */}
          {(isAdminAny || isSupervisor) && (
            <Drawer title="Performance Reviews" icon={Star}
              open={openDrawers.performance} onToggle={() => toggleDrawer('performance')}
              count={profileHook.performanceReviews.length}>
              <YearSelector year={perfYear} onChange={setPerfYear} />
              <TabBar
                tabs={[
                  { id: 'analytics', label: 'Analytics' },
                  { id: 'reviews', label: 'Reviews' },
                  { id: 'interview_notes', label: 'Interview Notes' },
                ]}
                active={performanceTab} onChange={setPerformanceTab}
              />

              {performanceTab === 'analytics' && (() => {
                // Compute projects worked on from time entries for the selected year
                const yearEntries = (activeTimeEntries || []).filter(e =>
                  String(e.userId) === String(userId) && e.date && e.date.startsWith(String(perfYear))
                );
                const projectIds = [...new Set(yearEntries.map(e => e.projectId).filter(Boolean))];
                const projectNames = projectIds.map(pid => {
                  const proj = (activeProjects || []).find(p => String(p.id) === String(pid));
                  return proj?.name || proj?.projectName || `#${pid}`;
                });

                // Compute avg weekly hours
                let avgWeeklyHours = null;
                if (yearEntries.length > 0) {
                  const totalHours = yearEntries.reduce((sum, e) => sum + ((Number(e.duration) || 0) / 3600), 0);
                  const dates = yearEntries.map(e => new Date(e.date)).filter(d => !isNaN(d));
                  if (dates.length > 0) {
                    const minDate = new Date(Math.min(...dates));
                    const maxDate = new Date(Math.max(...dates));
                    const weeks = Math.max(1, (maxDate - minDate) / (7 * 24 * 60 * 60 * 1000));
                    avgWeeklyHours = (totalHours / weeks).toFixed(1);
                  }
                }

                // Previous rating from evaluation reports (year before perfYear)
                const prevYearEvals = (profileHook.evaluationReports || []).filter(
                  e => e.year === perfYear - 1
                );
                const previousRating = prevYearEvals[0]?.performanceRating ?? prevYearEvals[0]?.averageRating ?? null;

                return (
                  <div style={{ padding: '16px 0' }}>
                    <FieldGrid cols={2}>
                      <div style={{ padding: '12px', background: BRIO.goldLight, borderRadius: 0 }}>
                        <div style={{ ...LABEL_STYLE, marginBottom: '6px' }}>Analytical Data (Read-only)</div>
                        <div style={{ fontSize: '12px', color: BRIO.black, lineHeight: 1.8 }}>
                          <div>Planning Masters: <span style={{ fontWeight: 500, color: BRIO.gold }}>{draft.planningMasters || 'no entry'}</span></div>
                          <div>Projects Worked On ({perfYear}): <span style={{ fontWeight: 500, color: BRIO.gold }}>
                            {projectIds.length > 0 ? `${projectIds.length} — ${projectNames.join(', ')}` : 'none'}
                          </span></div>
                          <div>Avg Weekly Hours: <span style={{ fontWeight: 500, color: BRIO.gold }}>
                            {avgWeeklyHours != null ? `${avgWeeklyHours}h` : '—'}
                          </span></div>
                          <div>Previous Rating ({perfYear - 1}): <span style={{ fontWeight: 500, color: BRIO.gold }}>
                            {previousRating != null ? `★${previousRating}` : '—'}
                          </span></div>
                        </div>
                      </div>
                    </FieldGrid>
                  </div>
                );
              })()}

              {performanceTab === 'reviews' && (
                <div style={{ padding: '16px 0' }}>
                  {reviewsForYear.length === 0 && (
                    <div style={{
                      padding: '16px', border: `1px dashed ${BRIO.border}`, borderRadius: 0,
                      textAlign: 'center', color: BRIO.greyMuted, fontSize: '12px', marginBottom: '12px',
                    }}>
                      <FileText style={{ width: 24, height: 24, margin: '0 auto 8px', opacity: 0.4 }} />
                      <div>No performance review entries for {perfYear}</div>
                    </div>
                  )}

                  {reviewsForYear.map(review => (
                    <ReviewPanel
                      key={review.id}
                      review={review}
                      onUpdate={(updates) => handleUpdateReview(review.id, updates)}
                      onDelete={() => handleDeleteReview(review.id)}
                      canWrite={editable}
                      saving={saving}
                      getCriteriaForReview={profileHook.getCriteriaForReview}
                      upsertCriterion={profileHook.upsertCriterion}
                      deleteCriterion={profileHook.deleteCriterion}
                      getAgreementsForReview={profileHook.getAgreementsForReview}
                      upsertAgreement={profileHook.upsertAgreement}
                      deleteAgreement={profileHook.deleteAgreement}
                      showToast={showToast}
                      activeUsers={reviewerUsers}
                    />
                  ))}

                  {editable && (
                    <button onClick={handleAddReview}
                      style={{
                        display: 'inline-flex', alignItems: 'center', gap: '6px',
                        padding: '8px 14px', marginTop: '4px',
                        background: BRIO.goldLight, border: `1px solid ${BRIO.goldMedium}`,
                        borderRadius: 0, color: BRIO.gold, fontSize: '12px',
                        fontFamily: BRIO.font, cursor: 'pointer',
                      }}>
                      <Plus style={{ width: 13, height: 13 }} /> add review
                    </button>
                  )}
                </div>
              )}

              {performanceTab === 'interview_notes' && (
                <div style={{ padding: '16px 0' }}>
                  {(!profileHook.notes || profileHook.notes.length === 0) && (
                    <div style={{
                      padding: '16px', border: `1px dashed ${BRIO.border}`, borderRadius: 0,
                      textAlign: 'center', color: BRIO.greyMuted, fontSize: '12px', marginBottom: '12px',
                    }}>
                      <FileText style={{ width: 24, height: 24, margin: '0 auto 8px', opacity: 0.4 }} />
                      <div>No interview notes recorded for this team member.</div>
                    </div>
                  )}

                  {(profileHook.notes || []).map(note => {
                    const dateStr = note.createdAt
                      ? new Date(note.createdAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
                      : '—';
                    const badgeColors = note.noteType === 'Hiring Interview'
                      ? { bg: 'rgba(132, 164, 140, 0.08)', text: 'var(--brio-green)', border: 'rgba(132, 164, 140, 0.3)' }
                      : { bg: 'var(--brio-grey-light)', text: 'var(--brio-grey)', border: 'var(--brio-border)' };

                    return (
                      <div key={note.id} style={{
                        marginBottom: '12px', border: `1px solid ${BRIO.border}`,
                        borderRadius: 0, overflow: 'hidden',
                      }}>
                        {/* Note header */}
                        <div style={{
                          display: 'flex', alignItems: 'center', gap: '10px',
                          padding: '10px 14px', background: BRIO.greyLight,
                          borderBottom: `1px solid ${BRIO.border}`,
                        }}>
                          <span style={{ fontSize: '12px', color: BRIO.grey, fontWeight: 500, fontFamily: BRIO.font }}>
                            {dateStr}
                          </span>
                          <span style={{
                            fontSize: '10px', fontWeight: 500, padding: '2px 8px',
                            borderRadius: '10px', background: badgeColors.bg,
                            color: badgeColors.text, border: `1px solid ${badgeColors.border}`,
                            whiteSpace: 'nowrap', fontFamily: BRIO.font,
                          }}>
                            {note.noteType}
                          </span>
                        </div>
                        {/* Note body */}
                        <pre style={{
                          margin: 0, padding: '12px 14px',
                          fontSize: '12px', lineHeight: '1.6',
                          color: BRIO.black, fontFamily: BRIO.font,
                          whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                          background: BRIO.white,
                        }}>
                          {note.note}
                        </pre>
                      </div>
                    );
                  })}
                </div>
              )}
            </Drawer>
          )}

          {/* ════════════════════════════════════════════════════════
              DRAWER 5: EMPLOYEE EVALUATION REPORT (role-based)
              ════════════════════════════════════════════════════════ */}
          {(isAdminAny || isSupervisor) && (
            <Drawer title="Employee Evaluation Report" icon={FileText}
              open={openDrawers.evaluation} onToggle={() => toggleDrawer('evaluation')}>

              {/* Supervisors only see content for members of their own team.
                  isInSupervisorsTeam is derived from weekly_team_assignments
                  (same source as TimesheetOverview), so it is reliable. */}
              {isSupervisor && !isInSupervisorsTeam ? (
                <div style={{ padding: '20px', textAlign: 'center', color: BRIO.greyMuted, fontSize: '13px' }}>
                  Evaluation reports are restricted to the member's direct supervisor.
                </div>
              ) : (
                <>
              <YearSelector year={evalYear} onChange={setEvalYear} />
              <TabBar
                tabs={[
                  { id: 'entries', label: 'Entries' },
                  { id: 'summary', label: 'Summary' },
                ]}
                active={evaluationTab} onChange={setEvaluationTab}
              />

              {evaluationTab === 'entries' && (
                <div style={{ padding: '16px 0' }}>
                  {evaluationsForYear.length === 0 && (
                    <div style={{
                      padding: '16px', border: `1px dashed ${BRIO.border}`, borderRadius: 0,
                      textAlign: 'center', color: BRIO.greyMuted, fontSize: '12px', marginBottom: '12px',
                    }}>
                      <Star style={{ width: 24, height: 24, margin: '0 auto 8px', opacity: 0.4 }} />
                      <div>No evaluation entries for {evalYear}</div>
                    </div>
                  )}

                  {evaluationsForYear.map(ev => (
                    <EvaluationCard
                      key={ev.id}
                      evaluation={ev}
                      editable={evalEditable}
                      getRoleForDate={getEvalRoleForDate}
                      onUpdate={handleUpdateEvaluation}
                      onDelete={handleDeleteEvaluation}
                      reviewerUsers={reviewerUsers}
                    />
                  ))}

                  {evalEditable && (
                    <button onClick={handleAddEvaluation}
                      style={{
                        display: 'inline-flex', alignItems: 'center', gap: '6px',
                        padding: '8px 14px', marginTop: '4px',
                        background: BRIO.goldLight, border: `1px solid ${BRIO.goldMedium}`,
                        borderRadius: 0, color: BRIO.gold, fontSize: '12px',
                        fontFamily: BRIO.font, cursor: 'pointer',
                      }}>
                      <Plus style={{ width: 13, height: 13 }} /> add evaluation
                    </button>
                  )}
                </div>
              )}

              {evaluationTab === 'summary' && (
                <div style={{ padding: '16px 0' }}>
                  <EvaluationSummaryPanel
                    evaluations={evaluationsForYear}
                    year={evalYear}
                    getRoleForDate={getEvalRoleForDate}
                  />
                </div>
              )}
                </>
              )}
            </Drawer>
          )}

          {/* ════════════════════════════════════════════════════════
              DRAWER 6: HIRING / CV (read-only snapshot from hire)
              ════════════════════════════════════════════════════════ */}
          {isAdminAny && draft.hiringCandidateId && (
            <Drawer title="Hiring / CV" icon={Briefcase}
              open={openDrawers.hiringCv} onToggle={() => toggleDrawer('hiringCv')}>

              {/* Location + portfolio + experience */}
              <FieldGrid cols={3}>
                <Field label="Home City">
                  <div style={{ padding: '7px 10px', background: BRIO.greyLight, borderRadius: 0, fontSize: '13px', color: BRIO.black, fontFamily: BRIO.font }}>
                    {draft.homeCity || '—'}
                  </div>
                </Field>
                <Field label="Years Experience">
                  <div style={{ padding: '7px 10px', background: BRIO.greyLight, borderRadius: 0, fontSize: '13px', color: BRIO.black, fontFamily: BRIO.font }}>
                    {draft.yearsExperience != null ? `${draft.yearsExperience} yrs` : '—'}
                  </div>
                </Field>
                <Field label="Notice Period End / Joining">
                  <div style={{ padding: '7px 10px', background: BRIO.greyLight, borderRadius: 0, fontSize: '13px', color: BRIO.black, fontFamily: BRIO.font }}>
                    {draft.noticePeriodEndDate || '—'}
                  </div>
                </Field>
                <Field label="Portfolio URL" span2>
                  {draft.portfolioUrl ? (
                    <a href={draft.portfolioUrl} target="_blank" rel="noopener noreferrer"
                      style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', color: 'var(--brio-gold)', fontSize: '13px', textDecoration: 'underline', padding: '7px 0' }}>
                      <ExternalLink style={{ width: 12, height: 12 }} />{draft.portfolioUrl}
                    </a>
                  ) : (
                    <div style={{ padding: '7px 10px', background: BRIO.greyLight, borderRadius: 0, fontSize: '13px', color: BRIO.greyMuted }}>—</div>
                  )}
                </Field>
              </FieldGrid>

              {/* Phone numbers */}
              <div style={SECTION_LABEL_STYLE}>Phone Numbers</div>
              <FieldGrid cols={3}>
                <Field label="Mobile">
                  <div style={{ padding: '7px 10px', background: BRIO.greyLight, borderRadius: 0, fontSize: '13px', color: BRIO.black, fontFamily: BRIO.font }}>
                    {draft.phoneMobile || '—'}
                  </div>
                </Field>
                <Field label="WhatsApp">
                  <div style={{ padding: '7px 10px', background: BRIO.greyLight, borderRadius: 0, fontSize: '13px', color: BRIO.black, fontFamily: BRIO.font }}>
                    {draft.phoneWhatsapp || '—'}
                  </div>
                </Field>
                <Field label={draft.phoneCustomLabel || 'Custom'}>
                  <div style={{ padding: '7px 10px', background: BRIO.greyLight, borderRadius: 0, fontSize: '13px', color: BRIO.black, fontFamily: BRIO.font }}>
                    {draft.phoneCustomValue || '—'}
                  </div>
                </Field>
              </FieldGrid>

              {/* Past employment */}
              {(draft.pastEmployment || []).length > 0 && (
                <>
                  <div style={SECTION_LABEL_STYLE}>Past Employment (from hiring)</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '12px' }}>
                    {(draft.pastEmployment || []).map((emp, idx) => (
                      <span key={idx} style={{
                        padding: '4px 10px', background: BRIO.greyLight,
                        color: BRIO.black, borderRadius: 0, fontSize: '12px',
                        fontFamily: BRIO.font, border: `1px solid ${BRIO.border}`,
                      }}>{emp}</span>
                    ))}
                  </div>
                </>
              )}

              {/* Software proficiency */}
              {Object.keys(draft.softwareProficiency || {}).length > 0 && (
                <>
                  <div style={SECTION_LABEL_STYLE}>Software Proficiency</div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '6px', marginBottom: '12px' }}>
                    {Object.entries(draft.softwareProficiency || {}).filter(([k]) => k !== 'others_text' && k !== 'others_level').map(([key, level]) => {
                      const levelLabels = { 1: 'Beginner', 2: 'Experienced', 3: 'Expert' };
                      const levelColors = {
                        1: { bg: 'rgba(236, 210, 136, 0.15)', text: 'var(--brio-orange)', border: 'var(--brio-yellow)' },
                        2: { bg: 'var(--brio-gold-light)', text: 'var(--brio-gold)', border: 'var(--brio-gold-light)' },
                        3: { bg: 'rgba(132, 164, 140, 0.08)', text: 'var(--brio-green)', border: 'rgba(132, 164, 140, 0.3)' },
                      };
                      const c = levelColors[level] || { bg: BRIO.greyLight, text: BRIO.grey, border: BRIO.border };
                      return (
                        <div key={key} style={{
                          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                          padding: '6px 10px', background: BRIO.white, borderRadius: 0,
                          border: `1px solid ${BRIO.border}`,
                        }}>
                          <span style={{ fontSize: '11px', color: BRIO.grey, textTransform: 'capitalize' }}>{key.replace(/_/g, ' ')}</span>
                          <span style={{
                            fontSize: '10px', padding: '2px 7px', borderRadius: 0,
                            background: c.bg, color: c.text, border: `1px solid ${c.border}`, fontWeight: 500,
                          }}>{levelLabels[level] || level}</span>
                        </div>
                      );
                    })}
                    {(draft.softwareProficiency || {}).others_text && (
                      <div style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        padding: '6px 10px', background: BRIO.white, borderRadius: 0,
                        border: `1px solid ${BRIO.border}`, gridColumn: 'span 3',
                      }}>
                        <span style={{ fontSize: '11px', color: BRIO.grey }}>{(draft.softwareProficiency || {}).others_text}</span>
                        {(draft.softwareProficiency || {}).others_level && (
                          <span style={{ fontSize: '10px', padding: '2px 7px', borderRadius: 0, background: BRIO.greyLight, color: BRIO.grey, fontWeight: 500 }}>
                            {({ 1: 'Beginner', 2: 'Experienced', 3: 'Expert' })[(draft.softwareProficiency || {}).others_level] || ''}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </>
              )}

              {/* Salary + future plans */}
              <div style={SECTION_LABEL_STYLE}>Compensation & Plans</div>
              <FieldGrid cols={3}>
                <Field label="Salary Expectations (monthly)">
                  <div style={{ padding: '7px 10px', background: BRIO.greyLight, borderRadius: 0, fontSize: '13px', color: BRIO.black, fontFamily: BRIO.font }}>
                    {draft.salaryExpectationsMonthly != null ? `₹${Number(draft.salaryExpectationsMonthly).toLocaleString()}` : '—'}
                  </div>
                </Field>
                <Field label="Current Salary (monthly)">
                  <div style={{ padding: '7px 10px', background: BRIO.greyLight, borderRadius: 0, fontSize: '13px', color: BRIO.black, fontFamily: BRIO.font }}>
                    {draft.currentSalaryMonthly != null ? `₹${Number(draft.currentSalaryMonthly).toLocaleString()}` : '—'}
                  </div>
                </Field>
                <Field label="Future Plans">
                  <div style={{ padding: '7px 10px', background: BRIO.greyLight, borderRadius: 0, fontSize: '13px', color: BRIO.black, fontFamily: BRIO.font }}>
                    {draft.futurePlans || '—'}
                  </div>
                </Field>
              </FieldGrid>

              {/* Interview notes */}
              {(draft.hiringOverallComments || draft.hiringFinalVerdictNotes) && (
                <>
                  <div style={SECTION_LABEL_STYLE}>Interview Notes</div>
                  {draft.hiringOverallComments && (
                    <div style={{ marginBottom: '10px' }}>
                      <label style={{ ...LABEL_STYLE, fontSize: '10px' }}>Overall Comments</label>
                      <div style={{ padding: '10px 12px', background: BRIO.greyLight, borderRadius: 0, fontSize: '12px', color: BRIO.black, fontFamily: BRIO.font, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
                        {draft.hiringOverallComments}
                      </div>
                    </div>
                  )}
                  {draft.hiringFinalVerdictNotes && (
                    <div>
                      <label style={{ ...LABEL_STYLE, fontSize: '10px' }}>Final Verdict Notes</label>
                      <div style={{ padding: '10px 12px', background: BRIO.greyLight, borderRadius: 0, fontSize: '12px', color: BRIO.black, fontFamily: BRIO.font, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
                        {draft.hiringFinalVerdictNotes}
                      </div>
                    </div>
                  )}
                </>
              )}
            </Drawer>
          )}

          {/* ── Access denied for 'user' role ──── */}
          {!isAdminAny && !isSupervisor && (
            <div style={{ padding: '48px 24px', textAlign: 'center', color: BRIO.greyMuted }}>
              <Shield style={{ width: 32, height: 32, margin: '0 auto 12px', opacity: 0.3 }} />
              <div style={{ fontSize: '14px', fontWeight: 500, color: BRIO.black }}>Access Restricted</div>
              <div style={{ fontSize: '12px', marginTop: '4px' }}>
                You do not have permission to view detailed member profiles.
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── Toast ──────────────────────────────────────────────── */}
      {toast && <Toast message={toast.message} type={toast.type} onDismiss={() => setToast(null)} />}

      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}


// ============================================================================
// PERFORMANCE REVIEW — Constants
// ============================================================================
const DEFAULT_CRITERIA = [
  'Quality of work', 'Speed & reliability', 'Ownership & accountability',
  'Communication', 'Collaboration', 'Initiative',
  'Professionalism', 'Learning & adaptability',
];
const REVIEW_TYPES = ['Probation', '3-month', '6-month', 'Annual', 'Ad-hoc'];
const AGREEMENT_STATUSES = ['Not started', 'In progress', 'Achieved', 'Blocked'];
const AGREEMENT_OWNERS = ['employee', 'supervisor', 'both'];
const PROMOTION_OPTIONS = ['Not now', 'Possible', 'Ready'];

// ============================================================================
// EVALUATION CARD — role-based criteria with 1-5 ratings
// ============================================================================
function EvaluationCard({ evaluation, editable, getRoleForDate, onUpdate, onDelete, reviewerUsers }) {
  // Resolve role from role history based on this evaluation's date
  const roleKey = getRoleForDate(evaluation.evaluationDate);
  const template = roleKey ? ROLE_EVALUATION_CRITERIA[roleKey] : null;
  const criteria = evaluation.criteria || {};

  // Compute category averages and overall average from criteria
  const categoryAverages = useMemo(() => {
    if (!template) return {};
    const avgs = {};
    template.forEach(cat => {
      const catRatings = criteria[cat.category] || {};
      const scores = cat.items.map(item => catRatings[item]).filter(v => v != null);
      avgs[cat.category] = scores.length > 0
        ? (scores.reduce((s, v) => s + v, 0) / scores.length).toFixed(1)
        : null;
    });
    return avgs;
  }, [template, criteria]);

  const overallAvg = useMemo(() => {
    if (!template) return evaluation.performanceRating;
    const allScores = [];
    template.forEach(cat => {
      const catRatings = criteria[cat.category] || {};
      cat.items.forEach(item => {
        if (catRatings[item] != null) allScores.push(catRatings[item]);
      });
    });
    return allScores.length > 0
      ? (allScores.reduce((s, v) => s + v, 0) / allScores.length).toFixed(1)
      : null;
  }, [template, criteria, evaluation.performanceRating]);

  const handleCriterionScore = (category, item, score) => {
    const updatedCriteria = { ...criteria };
    updatedCriteria[category] = { ...(updatedCriteria[category] || {}) };
    updatedCriteria[category][item] = score;
    // Recompute overall average
    const allScores = [];
    if (template) {
      template.forEach(cat => {
        const catRatings = updatedCriteria[cat.category] || {};
        cat.items.forEach(itm => {
          if (catRatings[itm] != null) allScores.push(catRatings[itm]);
        });
      });
    }
    const avg = allScores.length > 0
      ? Number((allScores.reduce((s, v) => s + v, 0) / allScores.length).toFixed(2))
      : null;
    onUpdate(evaluation.id, { criteria: updatedCriteria, performanceRating: avg });
  };

  // When the date changes, re-initialize criteria if the role changed
  const handleDateBlur = () => {
    if (dateLocal === evaluation.evaluationDate) return;
    const newRoleKey = getRoleForDate(dateLocal);
    const oldRoleKey = getRoleForDate(evaluation.evaluationDate);
    const changes = { evaluationDate: dateLocal };
    // If role changed, rebuild criteria from the new template
    if (newRoleKey !== oldRoleKey) {
      const newTemplate = newRoleKey ? ROLE_EVALUATION_CRITERIA[newRoleKey] : null;
      const newCriteria = {};
      if (newTemplate) {
        newTemplate.forEach(cat => {
          const catRatings = {};
          cat.items.forEach(item => { catRatings[item] = null; });
          newCriteria[cat.category] = catRatings;
        });
      }
      changes.criteria = Object.keys(newCriteria).length > 0 ? newCriteria : null;
      changes.performanceRating = null;
    }
    onUpdate(evaluation.id, changes);
  };

  const [notesLocal, setNotesLocal] = React.useState(evaluation.notes || '');
  const [dateLocal, setDateLocal] = React.useState(evaluation.evaluationDate || '');
  React.useEffect(() => { setNotesLocal(evaluation.notes || ''); }, [evaluation.notes]);
  React.useEffect(() => { setDateLocal(evaluation.evaluationDate || ''); }, [evaluation.evaluationDate]);

  return (
    <div style={{
      border: `1px solid ${BRIO.border}`, borderRadius: 0, padding: '14px 16px',
      marginBottom: '10px', background: BRIO.white,
    }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <span style={{ fontSize: '11px', color: BRIO.greyMuted }}>
            {evaluation.evaluationDate || 'No date'}
          </span>
          {roleKey && (
            <span style={{
              fontSize: '10px', fontWeight: 500, padding: '2px 8px',
              background: BRIO.goldLight, color: BRIO.gold,
              border: `1px solid ${BRIO.goldMedium}`,
            }}>{roleKey}</span>
          )}
          {overallAvg && (
            <span style={{
              fontSize: '10px', fontWeight: 700, padding: '2px 8px',
              background: 'rgba(132, 164, 140, 0.08)', color: 'var(--brio-green)',
              border: '1px solid rgba(132, 164, 140, 0.3)',
            }}>avg {overallAvg}</span>
          )}
        </div>
        {editable && onDelete && (
          <button onClick={() => onDelete(evaluation.id)} type="button"
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              color: BRIO.greyMuted, padding: '2px', opacity: 0.6,
            }}>
            <Trash2 style={{ width: 13, height: 13 }} />
          </button>
        )}
      </div>

      {/* Date field */}
      <div style={{ marginBottom: '12px' }}>
        <label style={LABEL_STYLE}>Evaluation Date</label>
        <input
          type="date" value={dateLocal} disabled={!editable}
          onChange={e => setDateLocal(e.target.value)}
          onBlur={handleDateBlur}
          style={{ ...INPUT_STYLE, maxWidth: '200px' }}
        />
      </div>

      {/* Role-based criteria */}
      {template ? (
        template.map(cat => {
          const catAvg = categoryAverages[cat.category];
          return (
            <div key={cat.category} style={{ marginBottom: '16px' }}>
              <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                ...SECTION_LABEL_STYLE, marginTop: '12px',
              }}>
                <span>{cat.category}</span>
                {catAvg && (
                  <span style={{
                    fontSize: '10px', fontWeight: 700, padding: '1px 6px',
                    background: BRIO.goldLight, color: BRIO.gold, textTransform: 'none',
                  }}>avg {catAvg}</span>
                )}
              </div>
              {cat.items.map((item, idx) => (
                <div key={idx} style={{
                  display: 'flex', alignItems: 'flex-start', gap: '10px',
                  padding: '8px 0', borderBottom: idx < cat.items.length - 1 ? `1px solid ${BRIO.greyLight}` : 'none',
                }}>
                  <div style={{ flex: 1, fontSize: '12px', color: BRIO.black, lineHeight: '1.5', fontFamily: BRIO.font }}>
                    {item}
                  </div>
                  <div style={{ flexShrink: 0 }}>
                    <ScoreSelector
                      value={(criteria[cat.category] || {})[item] ?? null}
                      onChange={v => handleCriterionScore(cat.category, item, v)}
                      disabled={!editable}
                    />
                  </div>
                </div>
              ))}
            </div>
          );
        })
      ) : (
        <div style={{
          padding: '12px', border: `1px dashed ${BRIO.border}`, borderRadius: 0,
          fontSize: '12px', color: BRIO.greyMuted, textAlign: 'center', marginBottom: '12px',
        }}>
          No role found in role history for this date — assign a role to enable criteria-based evaluation
        </div>
      )}

      {/* Reviewer + Notes */}
      <div style={{ display: 'flex', gap: '12px', marginTop: '8px', alignItems: 'flex-start' }}>
        <div style={{ minWidth: '180px' }}>
          <label style={LABEL_STYLE}>Reviewer</label>
          <select
            value={evaluation.reviewerName || ''}
            onChange={e => onUpdate(evaluation.id, { reviewerName: e.target.value || null })}
            disabled={!editable}
            style={SELECT_STYLE}
          >
            <option value="">Select reviewer…</option>
            {(reviewerUsers || []).map(u => (
              <option key={u.id} value={u.name}>{u.name}</option>
            ))}
          </select>
        </div>
        <div style={{ flex: 1 }}>
          <label style={LABEL_STYLE}>Notes / Observations</label>
          <AutoResizeTextarea
            value={notesLocal}
            onChange={e => setNotesLocal(e.target.value)}
            onBlur={() => { if (notesLocal !== evaluation.notes) onUpdate(evaluation.id, { notes: notesLocal }); }}
            disabled={!editable}
            placeholder="Additional notes…"
            style={{ ...INPUT_STYLE, resize: 'none' }}
          />
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// EVALUATION SUMMARY PANEL — aggregates across all entries for the year
// ============================================================================
function EvaluationSummaryPanel({ evaluations, year, getRoleForDate }) {
  // Group evaluations by the role active on their date
  const roleGroups = useMemo(() => {
    const groups = {};
    evaluations.forEach(ev => {
      const rk = getRoleForDate(ev.evaluationDate) || '__none__';
      if (!groups[rk]) groups[rk] = [];
      groups[rk].push(ev);
    });
    return groups;
  }, [evaluations, getRoleForDate]);

  // Build summary per role group
  const roleSummaries = useMemo(() => {
    return Object.entries(roleGroups).map(([rk, evs]) => {
      const template = rk !== '__none__' ? ROLE_EVALUATION_CRITERIA[rk] : null;
      if (!template) return { roleKey: rk, evs, catSummaries: null, overallAvg: null };

      const catSummaries = template.map(cat => {
        const itemSummaries = cat.items.map(item => {
          const scores = evs
            .map(ev => (ev.criteria || {})[cat.category]?.[item])
            .filter(v => v != null);
          const avg = scores.length > 0
            ? (scores.reduce((s, v) => s + v, 0) / scores.length).toFixed(1)
            : null;
          return { item, avg, count: scores.length };
        });
        const allCatScores = itemSummaries.filter(s => s.avg != null).map(s => Number(s.avg));
        const catAvg = allCatScores.length > 0
          ? (allCatScores.reduce((s, v) => s + v, 0) / allCatScores.length).toFixed(1)
          : null;
        return { category: cat.category, catAvg, items: itemSummaries };
      });

      const allAvgs = catSummaries.filter(c => c.catAvg != null).map(c => Number(c.catAvg));
      const overallAvg = allAvgs.length > 0
        ? (allAvgs.reduce((s, v) => s + v, 0) / allAvgs.length).toFixed(1)
        : null;

      return { roleKey: rk, evs, catSummaries, overallAvg };
    });
  }, [roleGroups]);

  if (evaluations.length === 0) {
    return (
      <div style={{
        padding: '16px', background: BRIO.goldLight, borderRadius: 0,
        fontSize: '12px', color: BRIO.grey,
      }}>
        <div style={{ fontWeight: 500, color: BRIO.black, marginBottom: '8px' }}>Evaluation Summary — {year}</div>
        <div>Total Entries: <span style={{ fontWeight: 500 }}>0</span></div>
        <div style={{ fontSize: '11px', fontStyle: 'italic', marginTop: '8px', color: BRIO.greyMuted }}>
          Summary auto-computed when evaluation entries exist.
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
      {roleSummaries.map(({ roleKey: rk, evs, catSummaries, overallAvg }) => (
        <div key={rk} style={{
          padding: '16px', background: BRIO.goldLight, borderRadius: 0,
          fontSize: '12px', color: BRIO.grey,
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
            <div style={{ fontWeight: 500, color: BRIO.black }}>Evaluation Summary — {year}</div>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              {rk !== '__none__' && (
                <span style={{
                  fontSize: '10px', fontWeight: 500, padding: '2px 8px',
                  background: BRIO.white, color: BRIO.gold,
                  border: `1px solid ${BRIO.goldMedium}`,
                }}>{rk}</span>
              )}
              {overallAvg && (
                <span style={{
                  fontSize: '11px', fontWeight: 700, padding: '2px 8px',
                  background: 'rgba(132, 164, 140, 0.08)', color: 'var(--brio-green)',
                  border: '1px solid rgba(132, 164, 140, 0.3)',
                }}>overall avg {overallAvg} / 5</span>
              )}
            </div>
          </div>

          <div>Entries: <span style={{ fontWeight: 500 }}>{evs.length}</span></div>

          {!catSummaries && (
            <div style={{ fontSize: '11px', fontStyle: 'italic', marginTop: '8px', color: BRIO.greyMuted }}>
              No criteria template for this role.
            </div>
          )}

          {catSummaries && catSummaries.map(cat => (
            <div key={cat.category} style={{ marginTop: '14px' }}>
              <div style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                fontSize: '11px', fontWeight: 600, color: BRIO.black,
                textTransform: 'uppercase', letterSpacing: '0.04em',
                paddingBottom: '4px', borderBottom: `1px solid ${BRIO.border}`, marginBottom: '6px',
              }}>
                <span>{cat.category}</span>
                {cat.catAvg && (
                  <span style={{ fontWeight: 700, color: BRIO.gold, textTransform: 'none' }}>{cat.catAvg} / 5</span>
                )}
              </div>
              {cat.items.map((it, idx) => (
                <div key={idx} style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  padding: '3px 0', fontSize: '11px',
                }}>
                  <span style={{ color: BRIO.black, flex: 1 }}>{it.item}</span>
                  <span style={{
                    fontWeight: 600, minWidth: '40px', textAlign: 'right',
                    color: it.avg != null ? BRIO.gold : BRIO.greyMuted,
                  }}>
                    {it.avg != null ? `${it.avg}` : '—'}
                  </span>
                </div>
              ))}
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

// ============================================================================
// SCORE SELECTOR (1–5 compact buttons)
// ============================================================================
function ScoreSelector({ value, onChange, disabled }) {
  const colors = {
    1: { bg: 'rgba(147, 73, 72, 0.08)', border: 'rgba(147, 73, 72, 0.2)', text: 'var(--brio-red)' },
    2: { bg: 'rgba(236, 210, 136, 0.15)', border: 'var(--brio-yellow)', text: 'var(--brio-orange)' },
    3: { bg: 'rgba(104, 149, 129, 0.08)', border: 'var(--brio-gold-light)', text: 'var(--brio-gold)' },
    4: { bg: 'rgba(132, 164, 140, 0.08)', border: 'rgba(132, 164, 140, 0.3)', text: 'var(--brio-green)' },
    5: { bg: 'rgba(132, 164, 140, 0.12)', border: 'rgba(132, 164, 140, 0.4)', text: 'var(--brio-green)' },
  };
  return (
    <div style={{ display: 'flex', gap: '3px' }}>
      {[1, 2, 3, 4, 5].map(n => {
        const active = value === n;
        const c = colors[n];
        return (
          <button key={n} type="button" disabled={disabled}
            onClick={() => onChange(active ? null : n)}
            style={{
              width: 26, height: 26, borderRadius: 0, fontSize: '11px', fontWeight: 700,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: active ? c.bg : BRIO.white,
              border: `1px solid ${active ? c.border : BRIO.border}`,
              color: active ? c.text : BRIO.greyMuted,
              cursor: disabled ? 'not-allowed' : 'pointer',
              opacity: disabled ? 0.5 : 1, transition: 'all 0.15s', fontFamily: BRIO.font,
            }}>
            {n}
          </button>
        );
      })}
    </div>
  );
}

// ============================================================================
// REVIEW ACCORDION
// ============================================================================
function ReviewAccordion({ title, open, onToggle, children, badge }) {
  return (
    <div style={{ border: `1px solid ${BRIO.border}`, borderRadius: 0, marginBottom: '6px', background: BRIO.white }}>
      <button onClick={onToggle} type="button"
        style={{
          width: '100%', display: 'flex', alignItems: 'center', gap: '8px',
          padding: '10px 14px', background: open ? BRIO.goldLight : 'transparent',
          border: 'none', cursor: 'pointer', fontFamily: BRIO.font,
          fontSize: '12px', fontWeight: open ? 500 : 400, color: BRIO.black,
          textTransform: 'lowercase', textAlign: 'left', transition: 'background 0.15s',
        }}>
        {open
          ? <ChevronDown style={{ width: 13, height: 13, color: BRIO.grey }} />
          : <ChevronRight style={{ width: 13, height: 13, color: BRIO.grey }} />}
        <span style={{ flex: 1 }}>{title}</span>
        {badge && <span style={{ fontSize: '10px', padding: '1px 7px', background: BRIO.goldMedium, color: BRIO.goldHover, borderRadius: '10px', fontWeight: 500 }}>{badge}</span>}
      </button>
      {open && <div style={{ padding: '10px 14px 14px', borderTop: `1px solid ${BRIO.border}` }}>{children}</div>}
    </div>
  );
}

// ============================================================================
// REVIEW PANEL — Collapsible review with sections B–F
// ============================================================================
function ReviewPanel({
  review, onUpdate, onDelete, canWrite, saving,
  getCriteriaForReview, upsertCriterion, deleteCriterion,
  getAgreementsForReview, upsertAgreement, deleteAgreement,
  showToast, activeUsers
}) {
  const [expanded, setExpanded] = useState(false);
  const [openSections, setOpenSections] = useState({});
  const [draft, setDraft] = useState({ ...review });

  useEffect(() => { setDraft({ ...review }); }, [review]);

  const criteria = getCriteriaForReview(review.id);
  const agreements = getAgreementsForReview(review.id);
  const sC = draft.sectionC || {};
  const sD = draft.sectionD || {};
  const sE = draft.sectionE || {};
  const sF = draft.sectionF || {};

  const toggleSection = (key) => setOpenSections(prev => ({ ...prev, [key]: !prev[key] }));
  const handleFieldChange = (field, value) => setDraft(prev => ({ ...prev, [field]: value }));
  const handleJsonField = (section, key, value) => {
    setDraft(prev => ({ ...prev, [section]: { ...(prev[section] || {}), [key]: value } }));
  };
  const handleSave = () => onUpdate(draft);

  const handleInitCriteria = async () => {
    if (criteria.length > 0) return;
    for (let i = 0; i < DEFAULT_CRITERIA.length; i++) {
      await upsertCriterion(review.id, {
        criterionName: DEFAULT_CRITERIA[i], score: null, strengthsEvidence: '', improvementNotes: '',
        isCustom: false, sortOrder: i,
      });
    }
  };
  const handleAddCustomCriterion = async () => {
    await upsertCriterion(review.id, {
      criterionName: 'Custom criterion', score: null, strengthsEvidence: '', improvementNotes: '',
      isCustom: true, sortOrder: criteria.length,
    });
    showToast('Custom criterion added');
  };
  const handleAddAgreement = async () => {
    await upsertAgreement(review.id, {
      agreementText: '', owner: '', startDate: '', dueDate: '',
      successMeasure: '', checkInDate: '', status: 'Not started', notes: '', sortOrder: agreements.length,
    });
    showToast('Agreement row added');
  };

  const scoredCriteria = criteria.filter(c => c.score != null);
  const avgScore = scoredCriteria.length > 0
    ? (scoredCriteria.reduce((s, c) => s + c.score, 0) / scoredCriteria.length).toFixed(1) : null;

  return (
    <div style={{ borderBottom: `1px solid ${BRIO.border}`, background: BRIO.white, marginBottom: '2px' }}>
      {/* Header */}
      <button onClick={() => setExpanded(!expanded)} type="button"
        style={{
          width: '100%', display: 'flex', alignItems: 'center', gap: '10px',
          padding: '12px 16px', background: expanded ? BRIO.goldLight : 'transparent',
          border: 'none', cursor: 'pointer', fontFamily: BRIO.font,
          fontSize: '13px', fontWeight: expanded ? 500 : 400,
          color: BRIO.black, textTransform: 'lowercase', textAlign: 'left', transition: 'background 0.15s',
        }}>
        {expanded ? <ChevronDown style={{ width: 14, height: 14, color: BRIO.grey }} /> : <ChevronRight style={{ width: 14, height: 14, color: BRIO.grey }} />}
        <span style={{ flex: 1 }}>
          review {review.reviewDate || '(no date)'}
          {review.reviewType && <span style={{ marginLeft: '6px', fontSize: '11px', color: BRIO.greyMuted }}>· {review.reviewType}</span>}
        </span>
        {avgScore && <span style={{ fontSize: '10px', padding: '2px 7px', background: BRIO.goldMedium, color: BRIO.goldHover, borderRadius: '10px', fontWeight: 500 }}>avg {avgScore}</span>}
        {review.overallRating != null && <span style={{ fontSize: '10px', padding: '2px 7px', background: 'rgba(132, 164, 140, 0.08)', color: 'var(--brio-green)', borderRadius: '10px', fontWeight: 500, border: '1px solid rgba(132, 164, 140, 0.3)' }}>★{review.overallRating}</span>}
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }} onClick={e => e.stopPropagation()}>
          {canWrite && <button onClick={onDelete} style={{ background: 'none', border: 'none', cursor: 'pointer', color: BRIO.greyMuted, padding: '4px' }} title="Delete review"><Trash2 style={{ width: 13, height: 13 }} /></button>}
        </div>
      </button>

      {expanded && (
        <div style={{ padding: '4px 16px 16px' }}>
          {/* Meta fields */}
          <FieldGrid cols={4}>
            <Field label="Review Date"><BrioDatePicker value={draft.reviewDate || ''} onChange={v => handleFieldChange('reviewDate', v)} disabled={!canWrite} /></Field>
            <Field label="Review Type">
              <select value={draft.reviewType || ''} onChange={e => handleFieldChange('reviewType', e.target.value)} disabled={!canWrite} style={SELECT_STYLE}>
                <option value="">Select…</option>
                {REVIEW_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </Field>
            <Field label="Overall Rating (1–5)"><ScoreSelector value={draft.overallRating} onChange={v => handleFieldChange('overallRating', v)} disabled={!canWrite} /></Field>
            <Field label="Confidence">
              <select value={draft.confidence || ''} onChange={e => handleFieldChange('confidence', e.target.value)} disabled={!canWrite} style={SELECT_STYLE}>
                <option value="">—</option><option value="Low">Low</option><option value="Med">Med</option><option value="High">High</option>
              </select>
            </Field>
          </FieldGrid>

          {/* ═══ SECTION B: Evaluation Criteria ═══ */}
          <ReviewAccordion title="B · evaluation criteria" open={openSections.B} badge={avgScore ? `avg ${avgScore}` : null}
            onToggle={() => { toggleSection('B'); handleInitCriteria(); }}>
            {criteria.map(crit => (
              <div key={crit.id} style={{ padding: '10px 0', borderBottom: `1px solid ${BRIO.border}` }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
                  {crit.isCustom
                    ? <input type="text" value={crit.criterionName} disabled={!canWrite} onChange={e => upsertCriterion(review.id, { ...crit, criterionName: e.target.value })} style={{ ...INPUT_STYLE, fontWeight: 500, flex: 1, fontSize: '12px' }} />
                    : <span style={{ fontSize: '12px', fontWeight: 500, color: BRIO.black, flex: 1 }}>{crit.criterionName}</span>}
                  <ScoreSelector value={crit.score} disabled={!canWrite} onChange={v => upsertCriterion(review.id, { ...crit, score: v })} />
                  {crit.isCustom && canWrite && <button onClick={() => deleteCriterion(crit.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: BRIO.greyMuted, padding: '2px' }}><Trash2 style={{ width: 12, height: 12 }} /></button>}
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                  <div>
                    <label style={{ ...LABEL_STYLE, fontSize: '10px' }}>Strengths / Evidence</label>
                    <AutoResizeTextarea value={crit.strengthsEvidence || ''} disabled={!canWrite} minRows={1} onChange={e => upsertCriterion(review.id, { ...crit, strengthsEvidence: e.target.value })} placeholder="Evidence of good performance…" />
                  </div>
                  <div>
                    <label style={{ ...LABEL_STYLE, fontSize: '10px' }}>Improvement Notes</label>
                    <AutoResizeTextarea value={crit.improvementNotes || ''} disabled={!canWrite} minRows={1} onChange={e => upsertCriterion(review.id, { ...crit, improvementNotes: e.target.value })} placeholder="Areas for improvement…" />
                  </div>
                </div>
              </div>
            ))}
            {canWrite && <button onClick={handleAddCustomCriterion} type="button" style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', marginTop: '10px', padding: '5px 12px', fontSize: '11px', fontFamily: BRIO.font, background: BRIO.greyLight, border: `1px solid ${BRIO.border}`, borderRadius: 0, color: BRIO.grey, cursor: 'pointer' }}><Plus style={{ width: 11, height: 11 }} /> add role-specific criterion</button>}
          </ReviewAccordion>

          {/* ═══ SECTION C: Discussion Points ═══ */}
          <ReviewAccordion title="C · discussion points" open={openSections.C} onToggle={() => toggleSection('C')}>
            <FieldGrid cols={1}>
              <Field label="Nature & Level of Work"><AutoResizeTextarea value={sC.natureAndLevelOfWork || ''} disabled={!canWrite} minRows={2} onChange={e => handleJsonField('sectionC', 'natureAndLevelOfWork', e.target.value)} placeholder="Describe the nature and level of work performed…" /></Field>
              <Field label="Experience of Work (incl. Collaboration)"><AutoResizeTextarea value={sC.experienceOfWork || ''} disabled={!canWrite} minRows={2} onChange={e => handleJsonField('sectionC', 'experienceOfWork', e.target.value)} placeholder="How has the employee experienced work and collaboration…" /></Field>
              <Field label="Preventing Factors"><AutoResizeTextarea value={sC.preventingFactors || ''} disabled={!canWrite} minRows={2} onChange={e => handleJsonField('sectionC', 'preventingFactors', e.target.value)} placeholder="What factors prevent optimal performance…" /></Field>
              <Field label="Future Plans"><AutoResizeTextarea value={sC.futurePlans || ''} disabled={!canWrite} minRows={2} onChange={e => handleJsonField('sectionC', 'futurePlans', e.target.value)} placeholder="Career development goals and future direction…" /></Field>
            </FieldGrid>
          </ReviewAccordion>

          {/* ═══ SECTION D: Problem Solving ═══ */}
          <ReviewAccordion title="D · problem solving (solution-focused)" open={openSections.D} badge={agreements.length > 0 ? `${agreements.length} agreements` : null} onToggle={() => toggleSection('D')}>
            <FieldGrid cols={1}>
              <Field label="Recognize the Problem"><AutoResizeTextarea value={sD.recognizeProblem || ''} disabled={!canWrite} minRows={2} onChange={e => handleJsonField('sectionD', 'recognizeProblem', e.target.value)} placeholder="What is the core issue or challenge…" /></Field>
              <Field label="Define Success"><AutoResizeTextarea value={sD.defineSuccess || ''} disabled={!canWrite} minRows={2} onChange={e => handleJsonField('sectionD', 'defineSuccess', e.target.value)} placeholder="What does a successful outcome look like…" /></Field>
              <Field label="Positive Exceptions"><AutoResizeTextarea value={sD.positiveExceptions || ''} disabled={!canWrite} minRows={2} onChange={e => handleJsonField('sectionD', 'positiveExceptions', e.target.value)} placeholder="Times when the problem was less severe or absent…" /></Field>
              <Field label="Short-Term Actions — Employee"><AutoResizeTextarea value={sD.shortTermActionsEmployee || ''} disabled={!canWrite} minRows={2} onChange={e => handleJsonField('sectionD', 'shortTermActionsEmployee', e.target.value)} placeholder="Concrete steps the employee will take…" /></Field>
              <Field label="Short-Term Actions — Supervisor"><AutoResizeTextarea value={sD.shortTermActionsSupervisor || ''} disabled={!canWrite} minRows={2} onChange={e => handleJsonField('sectionD', 'shortTermActionsSupervisor', e.target.value)} placeholder="Concrete steps the supervisor will take…" /></Field>
            </FieldGrid>
            {/* SMART Agreements */}
            <div style={{ ...SECTION_LABEL_STYLE, marginTop: '12px', fontSize: '11px' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span>SMART Agreements ({agreements.length})</span>
                {canWrite && <button onClick={handleAddAgreement} type="button" style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '4px 10px', fontSize: '10px', fontFamily: BRIO.font, background: BRIO.greyLight, border: `1px solid ${BRIO.border}`, borderRadius: 0, color: BRIO.grey, cursor: 'pointer' }}><Plus style={{ width: 10, height: 10 }} /> add</button>}
              </div>
            </div>
            {agreements.length === 0 && <div style={{ padding: '10px', border: `1px dashed ${BRIO.border}`, borderRadius: 0, textAlign: 'center', color: BRIO.greyMuted, fontSize: '11px' }}>No agreements yet</div>}
            {agreements.map(ag => (
              <div key={ag.id} style={{ padding: '10px', marginBottom: '6px', border: `1px solid ${BRIO.border}`, borderRadius: 0, background: BRIO.greyLight }}>
                <FieldGrid cols={3}>
                  <Field label="Agreement" span2><AutoResizeTextarea value={ag.agreementText || ''} disabled={!canWrite} minRows={1} onChange={e => upsertAgreement(review.id, { ...ag, agreementText: e.target.value })} placeholder="What was agreed…" /></Field>
                  <Field label="Owner"><select value={ag.owner || ''} onChange={e => upsertAgreement(review.id, { ...ag, owner: e.target.value })} disabled={!canWrite} style={SELECT_STYLE}><option value="">—</option>{AGREEMENT_OWNERS.map(o => <option key={o} value={o}>{o}</option>)}</select></Field>
                  <Field label="Start Date"><BrioDatePicker value={ag.startDate || ''} onChange={v => upsertAgreement(review.id, { ...ag, startDate: v })} disabled={!canWrite} /></Field>
                  <Field label="Due Date"><BrioDatePicker value={ag.dueDate || ''} onChange={v => upsertAgreement(review.id, { ...ag, dueDate: v })} disabled={!canWrite} /></Field>
                  <Field label="Status"><select value={ag.status || 'Not started'} onChange={e => upsertAgreement(review.id, { ...ag, status: e.target.value })} disabled={!canWrite} style={SELECT_STYLE}>{AGREEMENT_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}</select></Field>
                  <Field label="Success Measure" span2><input type="text" value={ag.successMeasure || ''} onChange={e => upsertAgreement(review.id, { ...ag, successMeasure: e.target.value })} disabled={!canWrite} style={INPUT_STYLE} placeholder="How to measure success…" /></Field>
                  <Field label="Check-in Date"><BrioDatePicker value={ag.checkInDate || ''} onChange={v => upsertAgreement(review.id, { ...ag, checkInDate: v })} disabled={!canWrite} /></Field>
                  <Field label="Notes" span2><AutoResizeTextarea value={ag.notes || ''} disabled={!canWrite} minRows={1} onChange={e => upsertAgreement(review.id, { ...ag, notes: e.target.value })} placeholder="Additional notes…" /></Field>
                  {canWrite && <div style={{ display: 'flex', alignItems: 'flex-end' }}><button onClick={() => deleteAgreement(ag.id)} style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '5px 10px', fontSize: '10px', fontFamily: BRIO.font, background: 'transparent', border: `1px solid ${BRIO.danger}33`, borderRadius: 0, color: BRIO.danger, cursor: 'pointer' }}><Trash2 style={{ width: 10, height: 10 }} /> remove</button></div>}
                </FieldGrid>
              </div>
            ))}
          </ReviewAccordion>

          {/* ═══ SECTION E: Future Criteria ═══ */}
          <ReviewAccordion title="E · future criteria for success" open={openSections.E} onToggle={() => toggleSection('E')}>
            <div style={{ ...SECTION_LABEL_STYLE, marginTop: 0, fontSize: '11px' }}>Top Success Criteria</div>
            {[0, 1, 2].map(i => {
              const items = sE.topSuccessCriteria || [{}, {}, {}];
              const item = items[i] || {};
              const updateItem = (field, value) => {
                const arr = [...(sE.topSuccessCriteria || [{}, {}, {}])];
                arr[i] = { ...arr[i], [field]: value };
                handleJsonField('sectionE', 'topSuccessCriteria', arr);
              };
              return (
                <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 120px 1fr', gap: '8px', marginBottom: '6px' }}>
                  <div><label style={{ ...LABEL_STYLE, fontSize: '10px' }}>Criterion {i + 1}</label><input type="text" value={item.text || ''} onChange={e => updateItem('text', e.target.value)} disabled={!canWrite} style={INPUT_STYLE} placeholder="Success criterion…" /></div>
                  <div><label style={{ ...LABEL_STYLE, fontSize: '10px' }}>Due Date</label><BrioDatePicker value={item.dueDate || ''} onChange={v => updateItem('dueDate', v)} disabled={!canWrite} /></div>
                  <div><label style={{ ...LABEL_STYLE, fontSize: '10px' }}>Measure</label><input type="text" value={item.measure || ''} onChange={e => updateItem('measure', e.target.value)} disabled={!canWrite} style={INPUT_STYLE} placeholder="How measured…" /></div>
                </div>
              );
            })}
            <FieldGrid cols={2}>
              <Field label="Expected Role Level at Next Review"><AutoResizeTextarea value={sE.expectedRoleLevelNextReview || ''} disabled={!canWrite} minRows={1} onChange={e => handleJsonField('sectionE', 'expectedRoleLevelNextReview', e.target.value)} placeholder="e.g. Medior Architect…" /></Field>
              <Field label="Training & Support Plan"><AutoResizeTextarea value={sE.trainingSupportPlan || ''} disabled={!canWrite} minRows={1} onChange={e => handleJsonField('sectionE', 'trainingSupportPlan', e.target.value)} placeholder="Courses, mentoring, tools…" /></Field>
              <Field label="Next Review Date"><BrioDatePicker value={sE.nextReviewDate || ''} onChange={v => handleJsonField('sectionE', 'nextReviewDate', v)} disabled={!canWrite} /></Field>
              <Field label="Evidence to Review"><AutoResizeTextarea value={sE.evidenceToReview || ''} disabled={!canWrite} minRows={1} onChange={e => handleJsonField('sectionE', 'evidenceToReview', e.target.value)} placeholder="What evidence will be evaluated…" /></Field>
            </FieldGrid>
          </ReviewAccordion>

          {/* ═══ SECTION F: Summary & Sign-off ═══ */}
          <ReviewAccordion title="F · summary & sign-off" open={openSections.F} onToggle={() => toggleSection('F')}>
            <div style={{ ...SECTION_LABEL_STYLE, marginTop: 0, fontSize: '11px' }}>Key Strengths</div>
            {[0, 1, 2].map(i => {
              const arr = sF.keyStrengths || ['', '', ''];
              return (
                <div key={i} style={{ display: 'flex', gap: '6px', alignItems: 'center', marginBottom: '4px' }}>
                  <span style={{ fontSize: '11px', color: BRIO.greyMuted, width: '16px', textAlign: 'right' }}>{i + 1}.</span>
                  <input type="text" value={arr[i] || ''} disabled={!canWrite} onChange={e => { const newArr = [...(sF.keyStrengths || ['', '', ''])]; newArr[i] = e.target.value; handleJsonField('sectionF', 'keyStrengths', newArr); }} style={{ ...INPUT_STYLE, flex: 1 }} placeholder={`Strength ${i + 1}…`} />
                </div>
              );
            })}
            <div style={{ ...SECTION_LABEL_STYLE, fontSize: '11px' }}>Key Risks</div>
            {[0, 1, 2].map(i => {
              const arr = sF.keyRisks || ['', '', ''];
              return (
                <div key={i} style={{ display: 'flex', gap: '6px', alignItems: 'center', marginBottom: '4px' }}>
                  <span style={{ fontSize: '11px', color: BRIO.greyMuted, width: '16px', textAlign: 'right' }}>{i + 1}.</span>
                  <input type="text" value={arr[i] || ''} disabled={!canWrite} onChange={e => { const newArr = [...(sF.keyRisks || ['', '', ''])]; newArr[i] = e.target.value; handleJsonField('sectionF', 'keyRisks', newArr); }} style={{ ...INPUT_STYLE, flex: 1 }} placeholder={`Risk ${i + 1}…`} />
                </div>
              );
            })}
            <FieldGrid cols={2}>
              <Field label="Overall Summary" span2><AutoResizeTextarea value={sF.overallSummary || ''} disabled={!canWrite} minRows={3} onChange={e => handleJsonField('sectionF', 'overallSummary', e.target.value)} placeholder="Final summary of performance…" /></Field>
              <Field label="Promotion Readiness">
                <select value={sF.promotionReadiness || ''} onChange={e => handleJsonField('sectionF', 'promotionReadiness', e.target.value)} disabled={!canWrite} style={SELECT_STYLE}>
                  <option value="">—</option>{PROMOTION_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                </select>
              </Field>
              <Field label="Employee Comments"><AutoResizeTextarea value={sF.employeeComments || ''} disabled={!canWrite} minRows={2} onChange={e => handleJsonField('sectionF', 'employeeComments', e.target.value)} placeholder="Employee's own comments…" /></Field>
            </FieldGrid>
            <div style={{ display: 'flex', gap: '20px', margin: '12px 0' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', fontFamily: BRIO.font, color: BRIO.black, cursor: canWrite ? 'pointer' : 'default' }}>
                <input type="checkbox" checked={sF.checkboxEmployeeUnderstands || false} disabled={!canWrite} onChange={e => handleJsonField('sectionF', 'checkboxEmployeeUnderstands', e.target.checked)} style={{ accentColor: BRIO.gold }} />Employee understands review
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', fontFamily: BRIO.font, color: BRIO.black, cursor: canWrite ? 'pointer' : 'default' }}>
                <input type="checkbox" checked={sF.checkboxSmartRecorded || false} disabled={!canWrite} onChange={e => handleJsonField('sectionF', 'checkboxSmartRecorded', e.target.checked)} style={{ accentColor: BRIO.gold }} />SMART agreements recorded
              </label>
            </div>
            <FieldGrid cols={3}>
              <Field label="Reviewer Sign-off">
                <select value={sF.reviewerSignoffName || ''} onChange={e => handleJsonField('sectionF', 'reviewerSignoffName', e.target.value)} disabled={!canWrite} style={SELECT_STYLE}>
                  <option value="">Select reviewer…</option>
                  {(activeUsers || []).filter(u => u.isActive !== false).map(u => <option key={u.id} value={u.name}>{u.name}</option>)}
                </select>
              </Field>
              <Field label="Employee Sign-off"><input type="text" value={sF.employeeSignoffName || ''} onChange={e => handleJsonField('sectionF', 'employeeSignoffName', e.target.value)} disabled={!canWrite} style={INPUT_STYLE} placeholder="Employee name" /></Field>
              <Field label="Sign-off Date"><BrioDatePicker value={sF.signoffDate || ''} onChange={v => handleJsonField('sectionF', 'signoffDate', v)} disabled={!canWrite} /></Field>
            </FieldGrid>
          </ReviewAccordion>

          {/* Save button */}
          {canWrite && (
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '14px' }}>
              <button onClick={handleSave} disabled={saving}
                style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 14px', background: BRIO.gold, color: BRIO.white, border: 'none', borderRadius: 0, fontSize: '12px', fontWeight: 500, fontFamily: BRIO.font, cursor: saving ? 'default' : 'pointer', opacity: saving ? 0.6 : 1, transition: 'all 0.2s' }}>
                <Save style={{ width: 13, height: 13 }} />{saving ? 'saving…' : 'save review'}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
